import { enableDemandForBtmSelection } from "../components/config/consumptionReadiness";
import AssetTypeField from "../components/AssetTypeField";
import { assetTypeDefinition, studyAssetIssue } from "../utils/projectAssets";
// src/pages/General.jsx
import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { useFile } from "../context/FileContext";
import { useDemo } from "../context/DemoContext";
import {
  getDemoWindSystemDisabledMessage,
  getRevenueModelDisabledMessage,
} from "../utils/demoAccess";
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  MenuItem,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormHelperText,
  Divider,
  CircularProgress,
  Alert,
  Box,
  Stack,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  Chip,
} from "@mui/material";
import { IconButton, InputAdornment } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import {
  FLOOR_INPUT_MODES,
  calculateTollingFloorPreview,
  isCapacityFloorInputMode,
  normalizeTollingFloorUpside,
} from "../components/config/tollingFloorUpside";
import {
  FIXED_CAPACITY_FEE_INPUT_MODES,
  FIXED_CAPACITY_PENALTY_MODES,
  FIXED_CAPACITY_SETTLEMENT_FREQUENCIES,
  MERCHANT_REVENUE_RECIPIENT_OPTIONS,
  POST_CONTRACT_REVENUE_MODE_OPTIONS,
  calculateFixedCapacityFeePreview,
  deriveInstalledBessPowerMw,
  normalizeTollingFixedCapacity,
} from "../components/config/tollingFixedCapacity";
import {
  HYBRID_PARTIAL_ALLOCATION_BASIS_OPTIONS,
  HYBRID_PARTIAL_FEE_INPUT_MODES,
  HYBRID_PARTIAL_PENALTY_MODES,
  HYBRID_PARTIAL_POST_CONTRACT_REVENUE_MODE_OPTIONS,
  HYBRID_PARTIAL_RECIPIENT_OPTIONS,
  HYBRID_PARTIAL_SETTLEMENT_FREQUENCIES,
  calculateHybridPartialPreview,
  normalizeHybridPartialTolling,
} from "../components/config/hybridPartialTolling";
import { useHistory } from "../context/HistoryContext";
import { useLanguage } from "../context/LanguageContext";
import MaybeTooltip from "../components/MaybeTooltip";
import SetupPageHeader from "../components/SetupPageHeader";
import MonthlyValueGrid from "../components/MonthlyValueGrid";
import { buildHeaders, downloadPvgisTmy, fetchSiteElevation, fetchParcelTerrain } from "../services/api";
import {
  BALANCING_PRODUCTS,
  BALANCING_V1_HELPER_TEXT,
  BALANCING_CONTRACT_CREDIT_SUPPORT_OPTIONS,
  BALANCING_CONTRACT_PRICE_UNIT_OPTIONS,
  BALANCING_CONTRACT_STATUS_OPTIONS,
  BALANCING_REVENUE_TREATMENT_HELPER_TEXT,
  BALANCING_REVENUE_TREATMENT_OPTIONS,
  BALANCING_CAPACITY_PRICE_UNIT_OPTIONS,
  DEFAULT_BALANCING_V1_CONFIG,
  getBalancingV1Preview,
  normalizeBalancingV1Config,
  validateBalancingV1Config,
  buildIncludedMarketsList,
} from "../components/config/balancingV1";
import {
  CAPACITY_PAYMENTS_HELPER_TEXT,
  CAPACITY_PAYMENT_TREATMENT_OPTIONS,
  DEFAULT_CAPACITY_PAYMENTS_CONFIG,
  normalizeCapacityPaymentsConfig,
  validateCapacityPaymentsConfig,
} from "../components/config/capacityPayments";
import {
  GUARANTEES_OF_ORIGIN_BASIS_OPTIONS,
  GUARANTEES_OF_ORIGIN_HELPER_TEXT,
  GUARANTEES_OF_ORIGIN_TREATMENT_OPTIONS,
  DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG,
  normalizeGuaranteesOfOriginConfig,
  validateGuaranteesOfOriginConfig,
} from "../components/config/guaranteesOfOrigin";
import {
  DEFAULT_PPA_CONFIG,
  applyPpaPreset,
  PPA_PRESET_OPTIONS,
  PPA_SETTLEMENT_STRUCTURE_OPTIONS,
  PPA_CFD_DIRECTION_OPTIONS,
  PPA_VOLUME_BASIS_OPTIONS,
  PPA_PRICE_SHAPE_OPTIONS,
  PPA_PRICE_UNIT_OPTIONS,
  PPA_PRICE_PROTECTION_OPTIONS,
  PPA_ESCALATION_MODE_OPTIONS,
  PPA_NEGATIVE_PRICE_RULE_OPTIONS,
  PPA_CAPTURE_RATE_MODE_OPTIONS,
  PPA_POST_CONTRACT_MODE_OPTIONS,
  PPA_TREATMENT_OPTIONS,
  PPA_APPLIES_TO_OPTIONS,
  PPA_YIELD_PROBABILITY_OPTIONS,
  PPA_HELPER_TEXT,
} from "../components/config/ppa";
import RegulatoryFrameworkSection from "../components/RegulatoryFrameworkSection";
import ConnectionCurtailmentSection from "../components/ConnectionCurtailmentSection";
import { normalizeConnectionCurtailment } from "../components/config/connectionCurtailment";
import { normalizeRegulatoryFramework } from "../components/config/regulatoryFramework";
import BtmSection from "../components/BtmSection";
import LocationPicker from "../components/LocationPicker";
import { ringFromParcel, parcelFromRing, ringAreaM2 } from "../utils/parcelGeometry";
import { alignBtmSwitches, normalizeBtm } from "../components/config/btm";
import { alignVirtualNetBillingLoad } from "../components/config/virtualNetBilling";
import {
  normalizeMarketZone,
  supportsIdaMarket,
  IDA_SUPPORTED_MARKETS,
  greekBalancingEligibility,
  GREEK_LARGE_STORAGE_MIN_INJECTION_MW,
  GREEK_LARGE_STORAGE_MIN_POWER_SHARE,
  GREEK_LARGE_STORAGE_MIN_ENERGY_SHARE,
  greekBalancingCriterionLetters,
} from "../utils/marketSupport";

// How long the form waits for the elevation service before it says the field
// can be typed in (see the elevation effect below).
export const ELEVATION_LOOKUP_TIMEOUT_MS = 20000;


// The address is kept in capitals. Greek written in capitals carries no
// accent marks (ΛΑΡΙΣΑ, not ΛΆΡΙΣΑ), so the tonos is dropped from Greek
// letters after upper-casing; a dialytika stays (ΑΫΛΟ), and Latin accents
// (é, ü) are left as they are.
function upperCaseAddress(value) {
  return String(value || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/([\u0391-\u03A9]\u0308?)\u0301/g, "$1")
    .normalize("NFC");
}

const HOUR_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m of [0, 15, 30, 45]) {
    HOUR_OPTIONS.push(
      `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
    );
  }
}


function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "EUR 0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0%";
  return `${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
}

function getSettlementPeriodLabel(settlementFrequency) {
  const normalized = String(settlementFrequency || "Monthly").toLowerCase();
  if (normalized.startsWith("annual")) return "year";
  if (normalized.startsWith("quarter")) return "quarter";
  return "month";
}

function getFloorAmountLabel(floorInputMode) {
  switch (floorInputMode) {
    case "eur_per_mw_year":
      return "Floor Rate (EUR/MW/year)";
    case "eur_per_mw_month":
      return "Floor Rate (EUR/MW/month)";
    case "eur_per_kw_month":
      return "Floor Rate (EUR/kW/month)";
    case "absolute_per_settlement_period":
    default:
      return "Floor Amount (EUR/settlement period)";
  }
}

function getFixedCapacityFeeAmountLabel(feeInputMode) {
  switch (feeInputMode) {
    case "eur_per_mw_month":
      return "Tolling Fee (EUR/MW/month)";
    case "eur_per_kw_month":
      return "Tolling Fee (EUR/kW/month)";
    case "absolute_per_year":
      return "Fixed Fee (EUR/year)";
    case "absolute_per_settlement_period":
      return "Fixed Fee (EUR/settlement period)";
    case "eur_per_mw_year":
    default:
      return "Tolling Fee (EUR/MW/year)";
  }
}

function getFixedCapacitySettlementPeriodLabel(settlementFrequency) {
  switch (String(settlementFrequency || "monthly").toLowerCase()) {
    case "annual":
      return "year";
    case "semiannual":
      return "half-year";
    case "quarterly":
      return "quarter";
    case "monthly":
    default:
      return "month";
  }
}

function getHybridPartialFeeAmountLabel(feeInputMode) {
  switch (feeInputMode) {
    case "eur_per_mw_month":
      return "Contracted Toll Fee (EUR/MW/month)";
    case "eur_per_kw_month":
      return "Contracted Toll Fee (EUR/kW/month)";
    case "absolute_per_year":
      return "Contracted Fixed Fee (EUR/year)";
    case "absolute_per_settlement_period":
      return "Contracted Fixed Fee (EUR/settlement period)";
    case "eur_per_mw_year":
    default:
      return "Contracted Toll Fee (EUR/MW/year)";
  }
}

const COUNTERPARTY_STATUS_OPTIONS = [
  { value: "generic_assumption", label: "Generic assumption" },
  { value: "indicative_discussion", label: "Indicative discussion" },
  { value: "non_binding_term_sheet", label: "Non-binding term sheet" },
  { value: "binding_term_sheet", label: "Binding term sheet" },
  { value: "signed_contract", label: "Signed contract" },
];

const COUNTERPARTY_TYPE_OPTIONS = [
  { value: "licensed_fose_aggregator", label: "Licensed FOSE / aggregator" },
  { value: "utility_trader", label: "Utility trader" },
  { value: "portfolio_optimizer", label: "Portfolio optimizer" },
  { value: "corporate_offtaker", label: "Corporate offtaker" },
  { value: "strategic_energy_company", label: "Strategic energy company" },
  { value: "other", label: "Other" },
  { value: "not_specified", label: "Not specified" },
];

const CREDIT_SUPPORT_OPTIONS = [
  { value: "none", label: "None" },
  { value: "parent_guarantee", label: "Parent guarantee" },
  { value: "letter_of_credit", label: "Letter of credit" },
  { value: "bank_guarantee", label: "Bank guarantee" },
  { value: "escrow", label: "Escrow" },
  { value: "other", label: "Other" },
];

const MERCHANT_VALUE_BASIS_OPTIONS = [
  { value: "net_after_charging_cost", label: "Net after charging cost" },
  { value: "gross_market_revenue", label: "Gross market revenue" },
  {
    value: "net_after_charging_and_market_fees",
    label: "Net after charging and market/grid fees",
  },
];

// Feature flag: set REACT_APP_IDA_BANKABILITY_ENABLED="true" at build time to enable IDA for GR projects.
// Absent / any other value → false. Production must stay false until backend IDA deployment is complete.
const IDA_BANKABILITY_ENABLED = process.env.REACT_APP_IDA_BANKABILITY_ENABLED === "true";
// Markets with IDA1/2/3 actuals loaded in the DB: GR via HEnEx, FR/DE/NL via
// the Nord Pool Data Portal, HU via HUPX Labs (history from the 2024-06-13 SIDC
// IDA go-live + daily scheduler). Extend when more zones are backfilled.
// MUST stay in sync with api/services/market_stack.py IDA_SUPPORTED_MARKETS.
// (εξάγεται από το utils/marketSupport — δείτε το σχόλιο εκεί)
const IDA_KEYS = new Set(["ida1", "ida2", "ida3"]);
const IDA_REQUIRES_DAM_HELPER =
  "IDA is modelled as a market-stack sensitivity on top of DAM in V1. DAM remains required when IDA products are selected.";

// A regulatory statement is the last place a pattern-matched translation may
// drift, so both languages are written out here rather than left to the
// DOM localisation bridge — which could not reach this sentence anyway: the
// bridge matches whole text nodes, and interpolated thresholds split one.
// The criterion letters follow the report (α/β/γ/δ in Greek, a/b/c/d in English).
function balancingBlockedMessage(failed, isGreek) {
  const letters = greekBalancingCriterionLetters(failed, isGreek);
  const minMw = GREEK_LARGE_STORAGE_MIN_INJECTION_MW;
  const powerPct = GREEK_LARGE_STORAGE_MIN_POWER_SHARE * 100;
  const energyPct = GREEK_LARGE_STORAGE_MIN_ENERGY_SHARE * 100;
  if (isGreek) {
    return (
      "Η εξισορρόπηση δεν είναι διαθέσιμη για αυτόν τον σταθμό. Στην Ελλάδα ισχύς εφεδρείας πωλείται " +
      "μόνο από Κατανεμόμενη Οντότητα Αποθήκευσης, και ένας σταθμός ΑΠΕ με ενσωματωμένη αποθήκευση " +
      "εμπίπτει αποκλειστικά στον Σταθμό ΑΠΕ με μεγάλη αποθήκευση (ορ. 145 του Κανονισμού " +
      "Αγοράς Εξισορρόπησης), που απαιτεί τέσσερα σωρευτικά κριτήρια: σύνδεση στο ΕΣΜΗΕ, " +
      `ισχύ έγχυσης αποθήκευσης τουλάχιστον ${minMw} MW, η ισχύς αυτή σε ${powerPct}% ή ` +
      "περισσότερο της Μέγιστης Καθαρής Ισχύος του σταθμού, και ωφέλιμη χωρητικότητα " +
      `αποθήκευσης σε ${energyPct}% ή περισσότερο μιας ώρας στη μέγιστη παραγωγή. ` +
      `Ο σταθμός δεν πληροί τα κριτήρια ${letters}, οπότε συμμετέχει μόνο με ευθύνη ` +
      "εξισορρόπησης. Η αναφορά το τεκμηριώνει με τις παραπομπές στα άρθρα."
    );
  }
  return (
    "Balancing is unavailable for this station. In Greece reserve capacity is sold only by a " +
    "dispatchable storage entity, and a RES station with integrated storage qualifies solely as a " +
    "Σταθμός ΑΠΕ με μεγάλη αποθήκευση (def. 145 of the Balancing Market Regulation), which " +
    "requires four cumulative criteria: a transmission (ΕΣΜΗΕ) connection, at least " +
    `${minMw} MW of storage injection power, that power at ${powerPct}% or more of the ` +
    `station's maximum net power, and usable storage capacity at ${energyPct}% or more of one ` +
    `hour at maximum generation. This station misses criteria ${letters}, so it participates ` +
    "with balance responsibility only. The report states this with the article references."
  );
}

const MARKET_REVENUE_OPTIONS = [
  { key: "dam",      label: "Day-Ahead (DAM)" },
  { key: "ida1",     label: "Intraday Auction 1" },
  { key: "ida2",     label: "Intraday Auction 2" },
  { key: "ida3",     label: "Intraday Auction 3" },
  { key: "balancing", label: "Balancing" },
  { key: "ancillary", label: "Ancillary" },
  { key: "capacity",  label: "Capacity payments" },
  { key: "go",        label: "Guarantees of Origin" },
  { key: "other",     label: "Other" },
];

function formatMw(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 MW";
  return `${amount.toLocaleString("en-US", { maximumFractionDigits: 3 })} MW`;
}

// isGR: legacy flag (project biddingZone === "GR"). IDA availability is decided
// from `biddingZone` via IDA_SUPPORTED_MARKETS below; isGR is only the fallback
// when no biddingZone was passed by an older call site.
function renderMarketRevenueStack(
  includedRevenueStreams,
  onChangeKey,
  context,
  isGR,
  balancingV1Config,
  onBalancingV1Change,
  installedBessPowerMw,
  biddingZone,
  capacityPaymentsConfig,
  onCapacityPaymentsChange,
  guaranteesOfOriginConfig,
  onGuaranteesOfOriginChange,
  regulatoryFrameworkMode = "none",
  stationContext = {},
  isGreek = false
) {
  const settlementIsArbitrage = context === "arbitrage";
  // IDA availability follows the project's market, not GR alone (isGR kept
  // for backwards-compatible call sites).
  // normalizeMarketZone, not a raw upper-case: a German project stores
  // biddingZone "DE_LU" (the ENTSO-E zone name) while IDA_SUPPORTED_MARKETS is
  // keyed by market code "DE", so the raw value made the IDA checkboxes read
  // "Not available for this market" for Germany even though its IDA1/2/3 prices
  // are ingested.
  const projectMarket = normalizeMarketZone(biddingZone || (isGR ? "GR" : ""));
  const idaForGR = IDA_BANKABILITY_ENABLED && IDA_SUPPORTED_MARKETS.has(projectMarket);
  const hasSelectedIda =
    idaForGR &&
    Boolean(
      includedRevenueStreams?.ida1 ||
      includedRevenueStreams?.ida2 ||
      includedRevenueStreams?.ida3
    );
  // Reserve capacity is sold only by a dispatchable storage entity. For a
  // Greek RES + storage hybrid the Balancing Market Regulation settles that by
  // construction (see greekBalancingEligibility), so the checkbox is greyed
  // out and forced off rather than letting a study book revenue the station
  // cannot sell. Every other market and every storage-only station is
  // untouched: applies=false leaves eligible=true.
  const balancingEligibility = greekBalancingEligibility({
    market: biddingZone || (isGR ? "GR" : ""),
    hasResGeneration: Boolean(stationContext.hasResGeneration),
    connectionLevel: stationContext.connectionLevel,
    storagePowerMw: installedBessPowerMw,
    storageEnergyMwh: stationContext.storageEnergyMwh,
    stationMaxNetPowerMw: stationContext.stationMaxNetPowerMw,
  });
  const balancingBlocked = balancingEligibility.applies && !balancingEligibility.eligible;
  const effectiveIncludedRevenueStreams = {
    ...(includedRevenueStreams || {}),
    dam: hasSelectedIda ? true : Boolean(includedRevenueStreams?.dam ?? true),
    ida1: idaForGR ? Boolean(includedRevenueStreams?.ida1) : false,
    ida2: idaForGR ? Boolean(includedRevenueStreams?.ida2) : false,
    ida3: idaForGR ? Boolean(includedRevenueStreams?.ida3) : false,
    balancing: balancingBlocked ? false : Boolean(includedRevenueStreams?.balancing),
    capacity: Boolean(includedRevenueStreams?.capacity),
    go: Boolean(includedRevenueStreams?.go),
  };
  // Same DE_LU -> DE normalization as projectMarket above: marketCode is stored
  // into balancingConfig.market/zoneCode and sent to the backend, which keys
  // balancing_reserve_prices by market code.
  const marketCode = normalizeMarketZone(biddingZone) || "GR";
  const balancingConfig = normalizeBalancingV1Config(
    balancingV1Config || DEFAULT_BALANCING_V1_CONFIG,
    marketCode
  );
  const balancingPreview = getBalancingV1Preview(
    balancingConfig,
    installedBessPowerMw,
    marketCode
  );
  const balancingValidation = validateBalancingV1Config({
    enabled: Boolean(effectiveIncludedRevenueStreams.balancing),
    includedMarkets: effectiveIncludedRevenueStreams,
    balancingConfig,
    installedBessPowerMw,
    market: marketCode,
  });
  const capacityConfig = normalizeCapacityPaymentsConfig(
    capacityPaymentsConfig || DEFAULT_CAPACITY_PAYMENTS_CONFIG
  );
  const capacityValidation = validateCapacityPaymentsConfig({
    enabled: Boolean(effectiveIncludedRevenueStreams.capacity),
    capacityConfig,
    settlementIsArbitrage,
    installedBessPowerMw,
  });
  const installedPowerForCapacity = Math.max(0, Number(installedBessPowerMw) || 0);
  const annualCapacityPaymentEur =
    installedPowerForCapacity * (Number(capacityConfig.eurPerMwYear) || 0);
  const goConfig = normalizeGuaranteesOfOriginConfig(
    guaranteesOfOriginConfig || DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG
  );
  const goValidation = validateGuaranteesOfOriginConfig({
    enabled: Boolean(effectiveIncludedRevenueStreams.go),
    goConfig,
    settlementIsArbitrage,
  });

  function getOptionMeta(key) {
    if (key === "dam") {
      return { chipLabel: "Active", chipColor: "success", disabled: false };
    }
    if (key === "balancing") {
      if (balancingBlocked) {
        return {
          chipLabel: isGreek
            ? "Μη επιλέξιμος βάσει του Κανονισμού Αγοράς Εξισορρόπησης"
            : "Not eligible under the Greek Balancing Market Regulation",
          chipColor: "warning",
          disabled: true,
        };
      }
      return { chipLabel: "Active when selected", chipColor: "success", disabled: false };
    }
    if (key === "capacity") {
      return { chipLabel: "Active when selected", chipColor: "success", disabled: false };
    }
    if (key === "go") {
      return { chipLabel: "Active when selected", chipColor: "success", disabled: false };
    }
    if (IDA_KEYS.has(key)) {
      if (idaForGR) {
        return { chipLabel: "Active when selected", chipColor: "success", disabled: false };
      }
      if (IDA_BANKABILITY_ENABLED && !IDA_SUPPORTED_MARKETS.has(projectMarket)) {
        return { chipLabel: "Not available for this market", chipColor: "warning", disabled: true };
      }
      return { chipLabel: "Backend pending", chipColor: "warning", disabled: true };
    }
    return { chipLabel: "Not modelled", chipColor: "default", disabled: true };
  }

  function buildNextRevenueStreams(key, checked) {
    const next = {
      ...effectiveIncludedRevenueStreams,
      [key]: checked,
    };
    if (IDA_KEYS.has(key) && checked) {
      next.dam = true;
    }
    if (key === "balancing" && checked) {
      next.dam = true;
    }
    if (key === "dam" && !checked && (next.ida1 || next.ida2 || next.ida3 || next.balancing)) {
      next.dam = true;
    }
    if (!idaForGR) {
      next.ida1 = false;
      next.ida2 = false;
      next.ida3 = false;
    }
    if (balancingBlocked) {
      next.balancing = false;
    }
    return next;
  }

  const hasActiveSelected = MARKET_REVENUE_OPTIONS.some(
    (o) => !getOptionMeta(o.key).disabled && Boolean(effectiveIncludedRevenueStreams?.[o.key])
  );

  const contextDescription =
    context === "arbitrage"
      ? "Select which markets are targeted by the DAM/IDA optimization. Only markets with Active status are included in the current run and export calculations."
      : context === "floor_upside"
      ? "Selected active markets determine floor coverage and upside above the floor. Pending or not-modelled markets are stored for future use and excluded from current run/export calculations."
      : "Select which revenue streams are included in the modelled merchant value. Unchecked streams are treated as excluded unless separately modelled.";

  const idaFootnote = idaForGR
    ? IDA_REQUIRES_DAM_HELPER
    : IDA_BANKABILITY_ENABLED && !IDA_SUPPORTED_MARKETS.has(projectMarket)
    ? null
    : "IDA bankability is not enabled in this environment yet.";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Market Revenue Stack
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {contextDescription}
      </Typography>
      {IDA_BANKABILITY_ENABLED && !IDA_SUPPORTED_MARKETS.has(projectMarket) && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
          IDA bankability simulation is available for{" "}
          {[...IDA_SUPPORTED_MARKETS].join(", ")}. IDA data are not yet loaded for this market.
        </Alert>
      )}
      {balancingBlocked && (
        <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
          {balancingBlockedMessage(balancingEligibility.failed, isGreek)}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1,
        }}
      >
        {MARKET_REVENUE_OPTIONS.map((option) => {
          const meta = getOptionMeta(option.key);
          return (
            <FormControlLabel
              key={option.key}
              sx={{ mr: 0, alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={Boolean(effectiveIncludedRevenueStreams?.[option.key])}
                  onChange={(e) => {
                    const nextStreams = buildNextRevenueStreams(option.key, e.target.checked);
                    onChangeKey(option.key, nextStreams[option.key], nextStreams);
                  }}
                  disabled={meta.disabled}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.5 }}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Chip
                    label={meta.chipLabel}
                    size="small"
                    color={meta.chipColor}
                    sx={{ height: 16, fontSize: "0.6rem", fontWeight: 600, width: "fit-content", "& .MuiChip-label": { px: 0.75 } }}
                  />
                </Box>
              }
            />
          );
        })}
      </Box>
      {!hasActiveSelected && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          No active market selected. At least one active market (e.g. DAM) is required for meaningful run results.
        </Alert>
      )}
      <FormHelperText sx={{ mt: 1 }}>
        Only markets with Active status are included in current run and export calculations.
      </FormHelperText>
      {idaFootnote && (
        <FormHelperText sx={{ mt: 0.25 }}>
          {idaFootnote}
        </FormHelperText>
      )}
      {effectiveIncludedRevenueStreams.balancing && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Balancing Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {BALANCING_V1_HELPER_TEXT}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              label="Price source"
              value="Historical reserve capacity prices (ENTSO-E)"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <MaybeTooltip
                      title="Reserve-capacity prices are taken from historical ENTSO-E balancing data; this source is fixed and not user-editable."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Market / Zone"
              value={marketCode}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <MaybeTooltip
                      title="Balancing bidding zone; it follows the project's market and selects which reserve-price series is used."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Capacity price unit</InputLabel>
              <Select
                label="Capacity price unit"
                value={balancingConfig.capacityPriceUnit || "EUR_PER_MW_PER_INTERVAL"}
                onChange={(e) =>
                  onBalancingV1Change?.({
                    ...balancingConfig,
                    capacityPriceUnit: e.target.value,
                  })
                }
              >
                {BALANCING_CAPACITY_PRICE_UNIT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                ENTSO-E reserve prices are quoted per ISP (settlement period). Use per-hour only if your source is an hourly rate.
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Balancing revenue treatment</InputLabel>
              <Select
                label="Balancing revenue treatment"
                value={balancingConfig.revenueTreatment}
                onChange={(e) =>
                  onBalancingV1Change?.({
                    ...balancingConfig,
                    revenueTreatment: e.target.value,
                  })
                }
              >
                {BALANCING_REVENUE_TREATMENT_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={
                      option.value === "CONTRACTED_BASE_CASE" &&
                      (!settlementIsArbitrage || regulatoryFrameworkMode !== "none")
                    }
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {BALANCING_REVENUE_TREATMENT_HELPER_TEXT}
                {!settlementIsArbitrage
                  ? " The contracted option is disabled here: tolling settlements allocate balancing through the treatments above."
                  : regulatoryFrameworkMode !== "none"
                  ? ` The contracted option is disabled because this project runs under regulatory Category ${regulatoryFrameworkMode}; no provision admitting contracted balancing to the base case has been verified for it.`
                  : ""}
              </FormHelperText>
            </FormControl>
          </Box>
          {balancingConfig.revenueTreatment === "CONTRACTED_BASE_CASE" && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                border: "1px dashed",
                borderColor: "warning.main",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel>Counterparty status</InputLabel>
                <Select
                  label="Counterparty status"
                  value={balancingConfig.contract.counterpartyStatus}
                  onChange={(e) =>
                    onBalancingV1Change?.({
                      ...balancingConfig,
                      contract: {
                        ...balancingConfig.contract,
                        counterpartyStatus: e.target.value,
                      },
                    })
                  }
                >
                  {BALANCING_CONTRACT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  A binding term sheet or signed contract is required for the base case.
                </FormHelperText>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Contract tenor (years)"
                value={balancingConfig.contract.tenorYears}
                onChange={(e) =>
                  onBalancingV1Change?.({
                    ...balancingConfig,
                    contract: {
                      ...balancingConfig.contract,
                      tenorYears: e.target.value,
                    },
                  })
                }
                inputProps={{ min: 1, step: 1 }}
                helperText="Contracted revenue enters the base case for this many years."
              />
              <TextField
                fullWidth
                size="small"
                label="Price basis"
                value={balancingConfig.contract.priceBasis}
                onChange={(e) =>
                  onBalancingV1Change?.({
                    ...balancingConfig,
                    contract: {
                      ...balancingConfig.contract,
                      priceBasis: e.target.value,
                    },
                  })
                }
                helperText="e.g. fixed EUR/MW/year; indexed to aFRR capacity auction results"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Credit support</InputLabel>
                <Select
                  label="Credit support"
                  value={balancingConfig.contract.creditSupport}
                  onChange={(e) =>
                    onBalancingV1Change?.({
                      ...balancingConfig,
                      contract: {
                        ...balancingConfig.contract,
                        creditSupport: e.target.value,
                      },
                    })
                  }
                >
                  {BALANCING_CONTRACT_CREDIT_SUPPORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  It may be &quot;None&quot;, but it must be declared.
                </FormHelperText>
              </FormControl>
              {BALANCING_PRODUCTS.filter(
                (productName) => balancingConfig.products[productName]?.enabled
              ).map((productName) => {
                const productEntry = balancingConfig.products[productName];
                const setContractPrice = (patch) =>
                  onBalancingV1Change?.({
                    ...balancingConfig,
                    products: {
                      ...balancingConfig.products,
                      [productName]: { ...productEntry, ...patch },
                    },
                  });
                return (
                  <Fragment key={`contract-price-${productName}`}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={`Contract price — ${productName} (optional)`}
                      value={productEntry.contractPriceValue ?? ""}
                      onChange={(e) =>
                        setContractPrice({
                          contractPriceValue:
                            e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      inputProps={{ min: 0, step: "any" }}
                      helperText="Enter only a price actually fixed in the contract or term sheet (e.g. 62,000 EUR/MW/year). Leave empty if the price is indexed to auction results or not yet agreed - the revenue is then valued at the published historical prices and the report says so. When every product has a price, the base case is valued at these prices instead."
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>{`Price unit — ${productName}`}</InputLabel>
                      <Select
                        label={`Price unit — ${productName}`}
                        value={productEntry.contractPriceUnit ?? "eur_per_mw_year"}
                        onChange={(e) =>
                          setContractPrice({ contractPriceUnit: e.target.value })
                        }
                        disabled={
                          productEntry.contractPriceValue === "" ||
                          productEntry.contractPriceValue == null
                        }
                      >
                        {BALANCING_CONTRACT_PRICE_UNIT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        EUR/MW per year is the usual contract quote; per hour is applied as-is.
                      </FormHelperText>
                    </FormControl>
                  </Fragment>
                );
              })}
            </Box>
          )}
          {BALANCING_PRODUCTS.map((productName) => {
            const productEntry = balancingConfig.products[productName];
            const setProductEntry = (patch) =>
              onBalancingV1Change?.({
                ...balancingConfig,
                products: {
                  ...balancingConfig.products,
                  [productName]: { ...productEntry, ...patch },
                },
              });
            return (
              <Box
                key={productName}
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: productEntry.enabled ? "success.main" : "divider",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "160px 180px 1fr 1fr" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(productEntry.enabled)}
                      onChange={(e) => setProductEntry({ enabled: e.target.checked })}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {productName}
                    </Typography>
                  }
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <MaybeTooltip
                    title="Offer up-regulation (discharge) reserve for this product; enables committing Reserved Up capacity."
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(productEntry.directions.up)}
                          onChange={(e) =>
                            setProductEntry({
                              directions: { ...productEntry.directions, up: e.target.checked },
                            })
                          }
                          disabled={!productEntry.enabled}
                          size="small"
                        />
                      }
                      label="Up"
                    />
                  </MaybeTooltip>
                  <MaybeTooltip
                    title="Offer down-regulation (charge) reserve for this product; enables committing Reserved Down capacity."
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(productEntry.directions.down)}
                          onChange={(e) =>
                            setProductEntry({
                              directions: { ...productEntry.directions, down: e.target.checked },
                            })
                          }
                          disabled={!productEntry.enabled}
                          size="small"
                        />
                      }
                      label="Down"
                    />
                  </MaybeTooltip>
                </Box>
                <TextField
                  label={`Reserved Up (${productName})`}
                  type="number"
                  size="small"
                  value={productEntry.reservedUpMw ?? ""}
                  onChange={(e) =>
                    setProductEntry({
                      reservedUpMw: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  disabled={!productEntry.enabled || !productEntry.directions.up}
                  inputProps={{ min: 0, max: installedBessPowerMw ?? undefined, step: "any" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        MW
                        <MaybeTooltip
                          title="MW of up-regulation (discharge) reserve you commit to the TSO for this product; this power is withheld from energy arbitrage."
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" tabIndex={-1}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </MaybeTooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={`Reserved Down (${productName})`}
                  type="number"
                  size="small"
                  value={productEntry.reservedDownMw ?? ""}
                  onChange={(e) =>
                    setProductEntry({
                      reservedDownMw: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  disabled={!productEntry.enabled || !productEntry.directions.down}
                  inputProps={{ min: 0, max: installedBessPowerMw ?? undefined, step: "any" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        MW
                        <MaybeTooltip
                          title="MW of down-regulation (charge) reserve you commit to the TSO for this product; this charging power is withheld from energy arbitrage."
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" tabIndex={-1}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </MaybeTooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            );
          })}
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: balancingConfig.activation?.enabled ? "success.main" : "divider",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr 1fr" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(balancingConfig.activation?.enabled)}
                  onChange={(e) =>
                    onBalancingV1Change?.({
                      ...balancingConfig,
                      activation: {
                        ...(balancingConfig.activation || {}),
                        enabled: e.target.checked,
                      },
                    })
                  }
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Activation-energy upside
                </Typography>
              }
            />
            <TextField
              label="Annual throughput cap"
              type="number"
              size="small"
              value={balancingConfig.activation?.maxThroughputMwh ?? ""}
              onChange={(e) =>
                onBalancingV1Change?.({
                  ...balancingConfig,
                  activation: {
                    ...(balancingConfig.activation || {}),
                    maxThroughputMwh: e.target.value === "" ? "" : Number(e.target.value),
                  },
                })
              }
              disabled={!balancingConfig.activation?.enabled}
              inputProps={{ min: 0, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    MWh/yr
                    <MaybeTooltip
                      title="Maximum balancing energy (MWh/year) the battery may activate; 0 leaves activation uncapped."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Cycle cost"
              type="number"
              size="small"
              value={balancingConfig.activation?.cycleCostEurPerMwh ?? ""}
              onChange={(e) =>
                onBalancingV1Change?.({
                  ...balancingConfig,
                  activation: {
                    ...(balancingConfig.activation || {}),
                    cycleCostEurPerMwh: e.target.value === "" ? "" : Number(e.target.value),
                  },
                })
              }
              disabled={!balancingConfig.activation?.enabled}
              inputProps={{ min: 0, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    €/MWh
                    <MaybeTooltip
                      title="Battery degradation cost per MWh of balancing throughput, netted from the activation-energy upside."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Round-trip efficiency"
              type="number"
              size="small"
              value={balancingConfig.activation?.roundTripEfficiency ?? ""}
              onChange={(e) =>
                onBalancingV1Change?.({
                  ...balancingConfig,
                  activation: {
                    ...(balancingConfig.activation || {}),
                    roundTripEfficiency: e.target.value === "" ? "" : Number(e.target.value),
                  },
                })
              }
              disabled={!balancingConfig.activation?.enabled}
              inputProps={{ min: 0, max: 1, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <MaybeTooltip
                      title="Charge-to-discharge efficiency used when valuing activation energy (e.g. 0.88 = 88%)."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText sx={{ gridColumn: "1 / -1" }}>
              Non-contracted activation-energy upside, priced from ADMIE settlement data as
              balancing-market arbitrage on the reserved MW, capped by annual throughput and
              net of cycle cost. Disclosure only — excluded from base-case NPV / IRR / DSCR.
            </FormHelperText>
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr))" },
              gap: 1,
            }}
          >
            {[
              ["Installed BESS Power", balancingPreview.installedPowerMw],
              ["Reserved Up", balancingPreview.reservedUpMw],
              ["Reserved Down", balancingPreview.reservedDownMw],
              ["Remaining discharge for DAM/IDA", balancingPreview.remainingDischargePowerMw],
              ["Remaining charge for DAM/IDA", balancingPreview.remainingChargePowerMw],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.default",
                  minHeight: 70,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {formatMw(value)}
                </Typography>
              </Box>
            ))}
          </Box>
          {balancingValidation.errors.map((message) => (
            <Alert key={message} severity="error" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
          {balancingValidation.warnings.map((message) => (
            <Alert key={message} severity="warning" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}
      {effectiveIncludedRevenueStreams.capacity && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Capacity Payments Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {CAPACITY_PAYMENTS_HELPER_TEXT}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              label="Capacity payment rate"
              type="number"
              value={capacityConfig.eurPerMwYear ?? ""}
              onChange={(e) =>
                onCapacityPaymentsChange?.({
                  ...capacityConfig,
                  eurPerMwYear: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              inputProps={{ min: 0, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    EUR/MW/yr
                    <MaybeTooltip
                      title="Fixed availability payment per MW of installed BESS discharge power per year (e.g. a capacity remuneration award)."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contract term"
              type="number"
              value={capacityConfig.termYears ?? ""}
              onChange={(e) =>
                onCapacityPaymentsChange?.({
                  ...capacityConfig,
                  termYears: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              inputProps={{ min: 1, step: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    years
                    <MaybeTooltip
                      title="Number of years the capacity payment is received; leave empty to apply it across the full project horizon."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Empty = full project horizon."
            />
            <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
              <InputLabel>Capacity payment treatment</InputLabel>
              <Select
                label="Capacity payment treatment"
                value={capacityConfig.revenueTreatment}
                onChange={(e) =>
                  onCapacityPaymentsChange?.({
                    ...capacityConfig,
                    revenueTreatment: e.target.value,
                  })
                }
              >
                {CAPACITY_PAYMENT_TREATMENT_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.value === "CONTRACTED_BASE_CASE" && !settlementIsArbitrage}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settlementIsArbitrage
                  ? "Contracted adds the amount to base-case NPV/IRR/DSCR; lenders will require the award as evidence."
                  : "Contracted treatment is available only for merchant arbitrage settlement; tolling structures use disclosure only."}
              </FormHelperText>
            </FormControl>
          </Box>
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.default",
              width: "fit-content",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Estimated annual capacity payment ({formatMw(installedPowerForCapacity)} installed)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              EUR {annualCapacityPaymentEur.toLocaleString("en-US", { maximumFractionDigits: 0 })} / year
            </Typography>
          </Box>
          {capacityValidation.errors.map((message) => (
            <Alert key={message} severity="error" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
          {capacityValidation.warnings.map((message) => (
            <Alert key={message} severity="warning" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}
      {effectiveIncludedRevenueStreams.go && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Guarantees of Origin Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {GUARANTEES_OF_ORIGIN_HELPER_TEXT}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              label="Guarantees of Origin price"
              type="number"
              value={goConfig.eurPerMwh ?? ""}
              onChange={(e) =>
                onGuaranteesOfOriginChange?.({
                  ...goConfig,
                  eurPerMwh: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              inputProps={{ min: 0, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    EUR/MWh
                    <MaybeTooltip
                      title="Green-certificate price added per MWh of eligible renewable energy (reference: DAPEEP GO auctions)."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contract term"
              type="number"
              value={goConfig.termYears ?? ""}
              onChange={(e) =>
                onGuaranteesOfOriginChange?.({
                  ...goConfig,
                  termYears: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              inputProps={{ min: 1, step: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    years
                    <MaybeTooltip
                      title="Number of years the Guarantees of Origin adder is received; leave empty to apply it across the full project horizon."
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Empty = full project horizon."
            />
            <FormControl fullWidth>
              <InputLabel>Eligible energy basis</InputLabel>
              <Select
                label="Eligible energy basis"
                value={goConfig.eligibleEnergyBasis}
                onChange={(e) =>
                  onGuaranteesOfOriginChange?.({
                    ...goConfig,
                    eligibleEnergyBasis: e.target.value,
                  })
                }
              >
                {GUARANTEES_OF_ORIGIN_BASIS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Net grid export counts only metered grid injection; gross PV generation counts total PV production.
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Guarantees of Origin treatment</InputLabel>
              <Select
                label="Guarantees of Origin treatment"
                value={goConfig.revenueTreatment}
                onChange={(e) =>
                  onGuaranteesOfOriginChange?.({
                    ...goConfig,
                    revenueTreatment: e.target.value,
                  })
                }
              >
                {GUARANTEES_OF_ORIGIN_TREATMENT_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.value === "CONTRACTED_BASE_CASE" && !settlementIsArbitrage}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settlementIsArbitrage
                  ? "Contracted adds the amount to base-case NPV/IRR/DSCR; reference price from DAPEEP GO auctions."
                  : "Contracted treatment is available only for merchant arbitrage settlement; under tolling / PPA the GO follows the energy to the offtaker."}
              </FormHelperText>
            </FormControl>
          </Box>
          {goValidation.errors.map((message) => (
            <Alert key={message} severity="error" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
          {goValidation.warnings.map((message) => (
            <Alert key={message} severity="warning" sx={{ mt: 1.5 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
}

const RESPONSIBILITY_OPTIONS = [
  { value: "project_owner", label: "Project owner" },
  { value: "trader_offtaker", label: "Trader/offtaker" },
  { value: "shared", label: "Shared" },
  { value: "mixed", label: "Mixed" },
  { value: "shared_or_trader", label: "Shared or trader/offtaker" },
  { value: "not_specified", label: "Not specified" },
];

function getFloorAmountHelper(floorInputMode) {
  if (floorInputMode === "absolute_per_settlement_period") {
    return "Guaranteed floor before availability adjustment.";
  }
  return "Contracted floor rate before settlement-frequency conversion and availability adjustment.";
}

function parseNumberInput(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** The engine's own table (BESS/base.py SURFACE_ALBEDOS), so the seed shown on
 *  screen is the number the run will use rather than a second opinion. */
export const SURFACE_ALBEDOS = {
  urban: 0.18,
  grass: 0.2,
  "fresh-grass": 0.26,
  soil: 0.17,
  sand: 0.4,
  snow: 0.65,
  "fresh-snow": 0.75,
  asphalt: 0.12,
  concrete: 0.3,
  aluminum: 0.85,
  copper: 0.74,
  "fresh-steel": 0.35,
  "dirty-steel": 0.08,
  gravel: 0.2,
  sea: 0.06,
};

const SURFACE_OPTIONS = [
  ["urban", "Urban"],
  ["grass", "Grass"],
  ["fresh-grass", "Fresh Grass"],
  ["soil", "Soil"],
  ["sand", "Sand"],
  ["snow", "Snow"],
  ["fresh-snow", "Fresh Snow"],
  ["asphalt", "Asphalt"],
  ["concrete", "Concrete"],
  ["aluminum", "Aluminum"],
  ["copper", "Copper"],
  ["fresh-steel", "Fresh Steel"],
  ["dirty-steel", "Dirty Steel"],
  ["gravel", "Gravel"],
  ["sea", "Sea"],
];

// The dropdown and reference use the same keys and model defaults.
const ALBEDO_REFERENCE = SURFACE_OPTIONS.map(([key, label]) => [
  label, SURFACE_ALBEDOS[key].toFixed(2),
]);

// Which settlement model holds the active market selection. Mirrors the
// per-model paths the settlementType switch already uses elsewhere in this file.
const SETTLEMENT_STREAM_MODEL_KEY = {
  Arbitrage: "arbitrageMerchant",
  TollingFixed: "tollingFixedCapacity",
  TollingFloorUpside: "tollingFloorUpside",
  HybridPartial: "hybridPartialTolling",
};

// The Greek balancing-eligibility test, asked of a whole config rather than of
// the market-stack panel's props, so the canonical list below can be built
// without the panel being on screen.
function configBalancingBlocked(config) {
  const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
  const totals = selected.reduce(
    (acc, battery) => {
      const count = Number(battery?.count);
      const safeCount = Math.max(0, Number.isFinite(count) ? count : 1);
      const powerKw = Number(battery?.p_max_d);
      const energyKwh = Number(battery?.energy);
      acc.powerKw += safeCount * Math.max(0, Number.isFinite(powerKw) ? powerKw : 0);
      acc.energyKwh += safeCount * Math.max(0, Number.isFinite(energyKwh) ? energyKwh : 0);
      return acc;
    },
    { powerKw: 0, energyKwh: 0 }
  );
  const systemType = String(config?.systemType || "Hybrid").trim().toLowerCase();
  const standalone =
    systemType === "standalone bess" ||
    (systemType.includes("standalone") && systemType.includes("bess"));
  const exportLimitKw = Number(config?.grid?.exportLimitKw);
  const verdict = greekBalancingEligibility({
    market: config?.biddingZone,
    hasResGeneration: !standalone,
    connectionLevel: config?.grid?.connectionLevel,
    storagePowerMw: totals.powerKw / 1000,
    storageEnergyMwh: totals.energyKwh / 1000,
    stationMaxNetPowerMw: Number.isFinite(exportLimitKw) ? exportLimitKw / 1000 : 0,
  });
  return verdict.applies && !verdict.eligible;
}

function withCanonicalMarketStack(config) {
  if (!config || typeof config !== "object") return config;
  // A station that cannot sell reserve capacity never declares it, whatever a
  // project saved under a different site left ticked. The checkbox is greyed
  // out for the same reason; this is the line that keeps the payload honest
  // when a stale selection arrives through "save as" rather than through a click.
  const blocked = configBalancingBlocked(config);
  // The run reads the selection from includedMarkets alone, so a leftover
  // marketStack.balancing.enabled is inert - but an exported config that still
  // advertises aFRR/mFRR products on a station that cannot sell them misleads
  // whoever reads the file. Clear the declaration with the selection.
  const staleBalancingDeclaration =
    blocked &&
    Boolean(config?.marketStack?.balancing) &&
    typeof config.marketStack.balancing === "object" &&
    config.marketStack.balancing.enabled !== false;
  const clearedBalancing = staleBalancingDeclaration
    ? { ...config.marketStack.balancing, enabled: false }
    : null;
  const modelKey = SETTLEMENT_STREAM_MODEL_KEY[config.settlementType];
  const streams = modelKey ? config?.[modelKey]?.includedRevenueStreams : null;
  // No model, or a model with no selection yet: leave the stored selection
  // alone rather than inventing one. Overwriting here would wipe a selection
  // the user has not reached yet - but a stale declaration still goes.
  if (!streams || typeof streams !== "object") {
    if (!clearedBalancing) return config;
    return {
      ...config,
      marketStack: { ...config.marketStack, balancing: clearedBalancing },
    };
  }
  const effectiveStreams = blocked ? { ...streams, balancing: false } : streams;
  const next = buildIncludedMarketsList(effectiveStreams);
  const current = config?.marketStack?.includedMarkets;
  const marketsUnchanged =
    Array.isArray(current) &&
    current.length === next.length &&
    current.every((market, index) => market === next[index]);
  if (marketsUnchanged && !clearedBalancing) return config;
  const marketStack = { ...(config.marketStack || {}), includedMarkets: next };
  if (clearedBalancing) marketStack.balancing = clearedBalancing;
  return { ...config, marketStack };
}

export default function General() {
  const history = useHistory();
  const { isGreek } = useLanguage();
  const { demoProfile } = useDemo();
  const { currentProject, activeAsset, updateAsset } = useFile();
  const cfg = useMemo(() => {
    return history.present ?? DEFAULT_CONFIG;
  }, [history.present]);
  const merchantDisabledMessage = getRevenueModelDisabledMessage(demoProfile, "Arbitrage");
  const ppaDisabledMessage = getRevenueModelDisabledMessage(demoProfile, "PPA");
  const tollingDisabledMessage = getRevenueModelDisabledMessage(
    demoProfile,
    "TollingFixed"
  );
  const selectedRevenueModelDisabledMessage = getRevenueModelDisabledMessage(
    demoProfile,
    cfg.settlementType
  );
  const windSystemDisabledMessage = getDemoWindSystemDisabledMessage(demoProfile);
  const systemTypeLabel = String(cfg.systemType || "Hybrid");
  const normalizedSystemType = systemTypeLabel.trim().toLowerCase();
  const isStandaloneBess =
    normalizedSystemType === "standalone bess" ||
    (normalizedSystemType.includes("standalone") && normalizedSystemType.includes("bess"));
  const tollingFloorUpside = useMemo(() => normalizeTollingFloorUpside(cfg), [cfg]);
  const tollingFixedCapacity = useMemo(() => normalizeTollingFixedCapacity(cfg), [cfg]);
  const hybridPartialTolling = useMemo(() => normalizeHybridPartialTolling(cfg), [cfg]);
  const ppa = useMemo(() => ({ ...DEFAULT_PPA_CONFIG, ...(cfg.ppa || {}) }), [cfg]);
  const regulatoryFramework = useMemo(() => normalizeRegulatoryFramework(cfg), [cfg]);
  // Flexible connection & curtailment (spec Feature 6). Generic across every
  // front-of-meter revenue model; never rendered for Behind-the-Meter.
  const connectionCurtailment = useMemo(() => normalizeConnectionCurtailment(cfg), [cfg]);
  const handleConnectionCurtailmentChange = (next) =>
    handleField("connectionCurtailment", next);
  const btm = useMemo(() => normalizeBtm(cfg), [cfg]);
  const tollingFloorPreview = useMemo(
    () => calculateTollingFloorPreview(tollingFloorUpside),
    [tollingFloorUpside]
  );
  const tollingFixedPreview = useMemo(
    () => calculateFixedCapacityFeePreview(tollingFixedCapacity, cfg),
    [cfg, tollingFixedCapacity]
  );
  const hybridPartialPreview = useMemo(
    () => calculateHybridPartialPreview(hybridPartialTolling, cfg),
    [cfg, hybridPartialTolling]
  );
  const isCapacityBasedFloorMode = isCapacityFloorInputMode(tollingFloorUpside.floorInputMode);
  const isAbsoluteFixedFeeMode =
    tollingFixedCapacity.feeInputMode === "absolute_per_year" ||
    tollingFixedCapacity.feeInputMode === "absolute_per_settlement_period";
  // biddingZone is the canonical GR marker — set by location auto-detect or user input
  const isProjectGR = (cfg.biddingZone || "").toUpperCase() === "GR";
  // Whether the project's market has ingested IDA1/2/3 prices (DE_LU -> DE aware).
  const idaMarketSupported = supportsIdaMarket(cfg.biddingZone);
  const installedBessPowerMw = useMemo(() => deriveInstalledBessPowerMw(cfg), [cfg]);
  // Everything the Greek balancing-eligibility test needs that the market
  // stack does not already receive. Built once here so all four settlement
  // models ask the same question of the same numbers.
  const stationContext = useMemo(() => {
    const selected = Array.isArray(cfg?.bess?.selected) ? cfg.bess.selected : [];
    const energyKwh = selected.reduce((sum, battery) => {
      const count = Number(battery?.count);
      const energy = Number(battery?.energy);
      return (
        sum +
        Math.max(0, Number.isFinite(count) ? count : 1) *
          Math.max(0, Number.isFinite(energy) ? energy : 0)
      );
    }, 0);
    const exportLimitKw = Number(cfg?.grid?.exportLimitKw);
    return {
      hasResGeneration: !isStandaloneBess,
      connectionLevel: cfg?.grid?.connectionLevel,
      storageEnergyMwh: energyKwh > 0 ? energyKwh / 1000 : 0,
      stationMaxNetPowerMw: Number.isFinite(exportLimitKw) ? exportLimitKw / 1000 : 0,
    };
  }, [cfg, isStandaloneBess]);
  const tollingValidation = useMemo(() => {
    const floorAmount = parseNumberInput(tollingFloorUpside.floorAmount);
    const upsideShare = parseNumberInput(tollingFloorUpside.projectUpsideSharePct);
    const contractTerm = parseNumberInput(tollingFloorUpside.contractTermYears);
    const availability = parseNumberInput(tollingFloorUpside.availabilityAdjustmentPct);
    const escalation = parseNumberInput(tollingFloorUpside.floorEscalationPct);
    const contractedCapacity = parseNumberInput(tollingFloorUpside.contractedCapacityMw);
    const bankability = tollingFloorUpside.bankability || {};
    const performanceRegime = tollingFloorUpside.performanceRegime || {};
    const creditSupportMonths = parseNumberInput(bankability.creditSupportMonths);
    const guaranteedAvailability = parseNumberInput(performanceRegime.guaranteedAvailabilityPct);
    const penaltyCap = parseNumberInput(performanceRegime.penaltyCapPctOfAnnualFloor);
    const plannedOutage = parseNumberInput(performanceRegime.plannedOutageAllowanceHours);
    const guaranteedRte = parseNumberInput(performanceRegime.guaranteedRtePct);
    const maxAnnualCycles = parseNumberInput(performanceRegime.maxAnnualCycles);
    const maxAnnualThroughput = parseNumberInput(performanceRegime.maxAnnualThroughputMWh);

    return {
      floorAmount:
        floorAmount == null || floorAmount < 0
          ? "Floor amount / rate must be greater than or equal to 0."
          : "",
      projectUpsideShare:
        upsideShare == null || upsideShare < 0 || upsideShare > 100
          ? "Project upside share must be between 0 and 100."
          : "",
      contractTerm:
        contractTerm == null || contractTerm <= 0
          ? "Contract term must be greater than 0."
          : "",
      availability:
        availability == null || availability < 0 || availability > 100
          ? "Availability adjustment must be between 0 and 100."
          : "",
      escalationWarning:
        escalation != null && (escalation < -10 || escalation > 10)
          ? "Warning: floor escalation is outside the typical -10% to +10% range."
          : "",
      contractedCapacity:
        isCapacityBasedFloorMode && (contractedCapacity == null || contractedCapacity <= 0)
          ? "Contracted capacity must be greater than 0 for capacity-based floor modes."
          : "",
      creditSupportMonths:
        creditSupportMonths != null && creditSupportMonths < 0
          ? "Credit support months must be greater than or equal to 0."
          : "",
      guaranteedAvailability:
        guaranteedAvailability != null &&
        (guaranteedAvailability < 0 || guaranteedAvailability > 100)
          ? "Guaranteed availability must be between 0 and 100."
          : "",
      penaltyCap:
        penaltyCap != null && penaltyCap < 0
          ? "Penalty cap must be greater than or equal to 0."
          : "",
      plannedOutage:
        plannedOutage != null && plannedOutage < 0
          ? "Planned outage allowance must be greater than or equal to 0."
          : "",
      guaranteedRte:
        guaranteedRte != null && (guaranteedRte < 0 || guaranteedRte > 100)
          ? "Guaranteed RTE must be between 0 and 100."
          : "",
      maxAnnualCycles:
        maxAnnualCycles != null && maxAnnualCycles < 0
          ? "Max annual cycles must be greater than or equal to 0."
          : "",
      maxAnnualThroughput:
        maxAnnualThroughput != null && maxAnnualThroughput < 0
          ? "Max annual throughput must be greater than or equal to 0."
          : "",
    };
  }, [isCapacityBasedFloorMode, tollingFloorUpside]);
  const fixedCapacityValidation = useMemo(() => {
    const feeAmount = parseNumberInput(tollingFixedCapacity.feeAmount);
    const contractTerm = parseNumberInput(tollingFixedCapacity.contractTermYears);
    const availability = parseNumberInput(tollingFixedCapacity.availabilityAdjustmentPct);
    const contractedCapacity = parseNumberInput(tollingFixedCapacity.contractedCapacityMw);
    const annualPenalty = parseNumberInput(tollingFixedCapacity.annualPenaltyEur);
    const penaltyPct = parseNumberInput(tollingFixedCapacity.penaltyPctOfGrossFee);
    const periodPenalty = parseNumberInput(tollingFixedCapacity.penaltyPerSettlementPeriodEur);
    const bankability = tollingFixedCapacity.bankability || {};
    const performanceRegime = tollingFixedCapacity.performanceRegime || {};
    const creditSupportMonths = parseNumberInput(bankability.creditSupportMonths);
    const guaranteedAvailability = parseNumberInput(performanceRegime.guaranteedAvailabilityPct);
    const penaltyCap = parseNumberInput(performanceRegime.penaltyCapPctOfAnnualFee);
    const plannedOutage = parseNumberInput(performanceRegime.plannedOutageAllowanceHours);
    const guaranteedRte = parseNumberInput(performanceRegime.guaranteedRtePct);
    const maxAnnualCycles = parseNumberInput(performanceRegime.maxAnnualCycles);
    const maxAnnualThroughput = parseNumberInput(performanceRegime.maxAnnualThroughputMWh);

    return {
      feeAmount:
        feeAmount != null && feeAmount < 0
          ? "Fee amount must be greater than or equal to 0."
          : "",
      contractTerm:
        contractTerm == null || contractTerm <= 0
          ? "Contract term must be greater than 0."
          : "",
      availability:
        availability != null && (availability < 0 || availability > 100)
          ? "Availability adjustment must be between 0 and 100."
          : "",
      contractedCapacity:
        !isAbsoluteFixedFeeMode &&
        tollingFixedCapacity.capacityBasis === "contracted_capacity_mw" &&
        (contractedCapacity == null || contractedCapacity <= 0)
          ? "Contracted capacity must be greater than 0."
          : "",
      contractedCapacityWarning:
        !isAbsoluteFixedFeeMode &&
        tollingFixedCapacity.capacityBasis === "contracted_capacity_mw" &&
        contractedCapacity != null &&
        installedBessPowerMw != null &&
        contractedCapacity > installedBessPowerMw
          ? "Warning: contracted capacity exceeds installed BESS power."
          : "",
      annualPenalty:
        annualPenalty != null && annualPenalty < 0
          ? "Annual penalty must be greater than or equal to 0."
          : "",
      penaltyPct:
        penaltyPct != null && penaltyPct < 0
          ? "Penalty percentage must be greater than or equal to 0."
          : "",
      periodPenalty:
        periodPenalty != null && periodPenalty < 0
          ? "Settlement-period penalty must be greater than or equal to 0."
          : "",
      creditSupportMonths:
        creditSupportMonths != null && creditSupportMonths < 0
          ? "Credit support months must be greater than or equal to 0."
          : "",
      guaranteedAvailability:
        guaranteedAvailability != null &&
        (guaranteedAvailability < 0 || guaranteedAvailability > 100)
          ? "Guaranteed availability must be between 0 and 100."
          : "",
      penaltyCap:
        penaltyCap != null && penaltyCap < 0
          ? "Penalty cap must be greater than or equal to 0."
          : "",
      plannedOutage:
        plannedOutage != null && plannedOutage < 0
          ? "Planned outage allowance must be greater than or equal to 0."
          : "",
      guaranteedRte:
        guaranteedRte != null && (guaranteedRte < 0 || guaranteedRte > 100)
          ? "Guaranteed RTE must be between 0 and 100."
          : "",
      maxAnnualCycles:
        maxAnnualCycles != null && maxAnnualCycles < 0
          ? "Max annual cycles must be greater than or equal to 0."
          : "",
      maxAnnualThroughput:
        maxAnnualThroughput != null && maxAnnualThroughput < 0
          ? "Max annual throughput must be greater than or equal to 0."
          : "",
    };
  }, [installedBessPowerMw, isAbsoluteFixedFeeMode, tollingFixedCapacity]);
  const hybridPartialValidation = useMemo(() => {
    const feeAmount = parseNumberInput(hybridPartialTolling.feeAmount);
    const contractedShare = parseNumberInput(hybridPartialTolling.contractedSharePct);
    const contractTerm = parseNumberInput(hybridPartialTolling.contractTermYears);
    const availability = parseNumberInput(hybridPartialTolling.availabilityAdjustmentPct);
    const contractedCapacity = parseNumberInput(hybridPartialTolling.contractedCapacityMw);
    const annualPenalty = parseNumberInput(hybridPartialTolling.annualPenaltyEur);
    const penaltyPct = parseNumberInput(hybridPartialTolling.penaltyPctOfGrossFee);
    const periodPenalty = parseNumberInput(hybridPartialTolling.penaltyPerSettlementPeriodEur);
    const bankability = hybridPartialTolling.bankability || {};
    const performanceRegime = hybridPartialTolling.performanceRegime || {};
    const creditSupportMonths = parseNumberInput(bankability.creditSupportMonths);
    const guaranteedAvailability = parseNumberInput(performanceRegime.guaranteedAvailabilityPct);
    const penaltyCap = parseNumberInput(performanceRegime.penaltyCapPctOfAnnualFee);
    const plannedOutage = parseNumberInput(performanceRegime.plannedOutageAllowanceHours);
    const guaranteedRte = parseNumberInput(performanceRegime.guaranteedRtePct);
    const maxAnnualCycles = parseNumberInput(performanceRegime.maxAnnualCycles);
    const maxAnnualThroughput = parseNumberInput(performanceRegime.maxAnnualThroughputMWh);

    return {
      contractedShare:
        contractedShare == null || contractedShare < 0 || contractedShare > 100
          ? "Contracted share must be between 0 and 100."
          : "",
      feeAmount:
        feeAmount != null && feeAmount < 0
          ? "Fee amount must be greater than or equal to 0."
          : "",
      contractTerm:
        contractTerm == null || contractTerm <= 0
          ? "Contract term must be greater than 0."
          : "",
      availability:
        availability != null && (availability < 0 || availability > 100)
          ? "Availability adjustment must be between 0 and 100."
          : "",
      contractedCapacity:
        hybridPartialTolling.capacityBasis === "contracted_capacity_mw" &&
        (contractedCapacity == null || contractedCapacity <= 0)
          ? "Contracted capacity must be greater than 0."
          : "",
      contractedCapacityWarning:
        hybridPartialTolling.capacityBasis === "contracted_capacity_mw" &&
        contractedCapacity != null &&
        installedBessPowerMw != null &&
        contractedCapacity > installedBessPowerMw
          ? "Warning: contracted capacity exceeds installed BESS power."
          : "",
      annualPenalty:
        annualPenalty != null && annualPenalty < 0
          ? "Annual penalty must be greater than or equal to 0."
          : "",
      penaltyPct:
        penaltyPct != null && penaltyPct < 0
          ? "Penalty percentage must be greater than or equal to 0."
          : "",
      periodPenalty:
        periodPenalty != null && periodPenalty < 0
          ? "Settlement-period penalty must be greater than or equal to 0."
          : "",
      creditSupportMonths:
        creditSupportMonths != null && creditSupportMonths < 0
          ? "Credit support months must be greater than or equal to 0."
          : "",
      guaranteedAvailability:
        guaranteedAvailability != null &&
        (guaranteedAvailability < 0 || guaranteedAvailability > 100)
          ? "Guaranteed availability must be between 0 and 100."
          : "",
      penaltyCap:
        penaltyCap != null && penaltyCap < 0
          ? "Penalty cap must be greater than or equal to 0."
          : "",
      plannedOutage:
        plannedOutage != null && plannedOutage < 0
          ? "Planned outage allowance must be greater than or equal to 0."
          : "",
      guaranteedRte:
        guaranteedRte != null && (guaranteedRte < 0 || guaranteedRte > 100)
          ? "Guaranteed RTE must be between 0 and 100."
          : "",
      maxAnnualCycles:
        maxAnnualCycles != null && maxAnnualCycles < 0
          ? "Max annual cycles must be greater than or equal to 0."
          : "",
      maxAnnualThroughput:
        maxAnnualThroughput != null && maxAnnualThroughput < 0
          ? "Max annual throughput must be greater than or equal to 0."
          : "",
    };
  }, [hybridPartialTolling, installedBessPowerMw]);


  const BASE_URL = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
  if (!BASE_URL) {
    console.error("❌ REACT_APP_API_BASE is not set");
  }
  const [tzLoading, setTzLoading] = useState(false);
  const lat = cfg?.loc_info?.lat;
  const lon = cfg?.loc_info?.lon;
  const lastCoordsRef = useRef({ lat: null, lon: null });
  const skipFirstFetchRef = useRef(true);
  const [tmyLoading, setTmyLoading] = useState(false);
  const [tmyError, setTmyError] = useState("");


  useEffect(() => {
    // Every time a different project is opened, do NOT auto-fetch timezone immediately
    skipFirstFetchRef.current = true;
    lastCoordsRef.current = { lat: null, lon: null };
  }, [currentProject?.id]);



  // ---- AUTO-DETECT TIMEZONE WHEN LAT/LON CHANGE ----
  const cfgRef = useRef(cfg);
  useEffect(() => { cfgRef.current = cfg; }, [cfg]);

  useEffect(() => {
    if (!BASE_URL) return;

    const latNum = Number(String(lat).replace(",", "."));
    const lonNum = Number(String(lon).replace(",", "."));
    if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return;

    // skip first render
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false;
      lastCoordsRef.current = { lat: latNum, lon: lonNum };
      return;
    }

    const prev = lastCoordsRef.current;
    if (prev.lat === latNum && prev.lon === lonNum) return;

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      // update "last" at the time we actually fetch (important)
      lastCoordsRef.current = { lat: latNum, lon: lonNum };

      try {
        setTzLoading(true);
        const res = await fetch(
          `${BASE_URL}/derive_timezone?lat=${latNum}&lon=${lonNum}`,
          {
            signal: controller.signal,
            headers: await buildHeaders(),
          }
        );
        if (!res.ok) return;
        const data = await res.json();

        if (data?.timeZone) {
          const cfgNow = cfgRef.current;
          history.commit({
            ...cfgNow,
            timeZone: data.timeZone,
            biddingZone: data.biddingZone || cfgNow.biddingZone || "",
            address: data.address || cfgNow.address || "",
          });
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      } finally {
        setTzLoading(false);
      }
    }, 600);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [lat, lon, BASE_URL, history]); // ✅ only coords + backend

  // When IDA is enabled and biddingZone switches to a market with NO ingested IDA
  // prices, clear any IDA selections.
  // Gated on idaMarketSupported, not isProjectGR: while this said "away from GR"
  // it wiped ida1/2/3 out of the stored config for every FR/DE/NL/HU project on
  // every render pass, so the checkboxes could not stay ticked even though those
  // markets have IDA data. normalizeMarketZone inside also covers DE_LU.
  useEffect(() => {
    if (!IDA_BANKABILITY_ENABLED) return;
    if (idaMarketSupported) return;
    const currentCfg = cfgRef.current;
    const hasIda = (rs) => Boolean(rs?.ida1 || rs?.ida2 || rs?.ida3);
    const clearIda = (rs) => ({ ...(rs || {}), ida1: false, ida2: false, ida3: false });
    const modelPaths = [
      ["tollingFloorUpside", "includedRevenueStreams"],
      ["tollingFixedCapacity", "includedRevenueStreams"],
      ["hybridPartialTolling", "includedRevenueStreams"],
      ["arbitrageMerchant", "includedRevenueStreams"],
    ];
    let nextCfg = currentCfg;
    let changed = false;
    for (const [model, field] of modelPaths) {
      const rs = nextCfg?.[model]?.[field];
      if (hasIda(rs)) {
        nextCfg = setPathInObject(nextCfg, `${model}.${field}`, clearIda(rs));
        changed = true;
      }
    }
    if (changed) history.commit(nextCfg);
  }, [idaMarketSupported]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- field helpers (just update local cfg; effect above propagates) ---
  // marketStack.includedMarkets is a SECOND copy of a selection whose truth
  // lives in <settlementModel>.includedRevenueStreams, and nothing kept the two
  // in step: a project created with "Save as" from another one inherited that
  // copy and carried it for ever, so an export of an 11B study that had only
  // DAM ticked still declared DAM, IDA1, IDA2, IDA3, BALANCING and CAPACITY.
  //
  // The run itself was never wrong - the run payload is rebuilt from the
  // streams every time - but the stored copy is what an export carries, what a
  // re-import would then believe, and what includedMarketsToMap turns into
  // all-true for the balancing validator. On an 11B study that is the one
  // market whose base-case treatment is not legally settled, so a stale copy
  // claiming it is exactly the wrong thing to ship.
  //
  // Derived here, at the single commit choke point, so every edit keeps the two
  // in step and no handler has to remember. Returning the same object when
  // nothing changed keeps undo/redo from gaining empty steps.
  const commit = (next) => history.commit(withCanonicalMarketStack(next));

  const setTop = (key, value) => {
    commit({ ...cfg, [key]: value });
  };

  const setLoc = (key, value) => {
    commit({
      ...cfg,
      loc_info: { ...(cfg.loc_info || {}), [key]: value },
    });
  };

  /**
   * Latitude and longitude together, in ONE commit.
   *
   * setLoc derives its next config from the `cfg` of the current render, so
   * calling it twice in a row -- setLoc("lat"), setLoc("lon") -- makes both
   * calls start from the same base and only the second survives. A map click
   * moves both at once, so it needs its own committer. This also keeps the
   * pick to a single undo step and fires the timezone lookup once instead of
   * twice.
   */
  const setCoords = (nextLat, nextLon) => {
    commit({
      ...cfg,
      loc_info: { ...(cfg.loc_info || {}), lat: nextLat, lon: nextLon },
    });
  };

  const handleField = (path, value) => {
    commit(setPathInObject(cfg, path, value));
  };

  // Regulatory framework (11A/11B) change with automatic gating, applied in a
  // single commit so undo/redo stays one step:
  //  - 11A: grid charging is forbidden -> allowGridCharging locked OFF, and the
  //    Market Revenue Stack is restricted to DAM (Reference Price + DAM is the
  //    only 11A energy settlement; the backend also validates this).
  //  - 11B / none: no forced changes.
  const handleRegulatoryFrameworkChange = (nextRf) => {
    // Entering 11A/11B from another mode: pre-enable the injection-cap
    // profile (a cap is intrinsic to both categories). The user can still
    // untick it deliberately afterwards.
    const enteredActiveMode =
      nextRf.mode !== regulatoryFramework.mode &&
      (nextRf.mode === "11A" || nextRf.mode === "11B");
    if (enteredActiveMode && !nextRf.injectionCap?.enabled) {
      nextRf = {
        ...nextRf,
        injectionCap: { ...(nextRf.injectionCap || {}), enabled: true },
      };
    }
    let nextCfg = setPathInObject(cfg, "arbitrageMerchant.regulatoryFramework", nextRf);
    if (nextRf.mode === "11A") {
      nextCfg = setPathInObject(nextCfg, "allowGridCharging", false);
      nextCfg = setPathInObject(nextCfg, "arbitrageMerchant.includedRevenueStreams", {
        dam: true,
        ida1: false,
        ida2: false,
        ida3: false,
        balancing: false,
        ancillary: false,
        capacity: false,
        go: false,
        other: false,
      });
    }
    commit(nextCfg);
  };

  // Behind-the-Meter decides the BESS page's grid switches: every BTM change
  // commits them at their implied value (see alignBtmSwitches in
  // config/btm.js), in the same commit so undo/redo stays one step:
  //  - Enable Grid Charging: ON for retail ToU arbitrage or a standalone
  //    battery (no other energy source), OFF otherwise;
  //  - Allow grid injection: OFF under zero feed-in, for a standalone battery,
  //    or when the surplus has no value; ON when export is permitted and
  //    valued (including 'export from PV only');
  //  - Simultaneous injecting & drawing: OFF (one meter);
  //  - Simultaneous PV + Grid Charging: cleared when there is no grid charging.
  // The BESS page shows each of them locked, with the reason under it.
  // Under virtual net billing with several supplies the project's consumption
  // is also set to the sum of the supplies' files (no change otherwise).
  const handleBtmChange = (nextBtm) => {
    commit(alignVirtualNetBillingLoad(alignBtmSwitches(setPathInObject(cfg, "btm", nextBtm))));
  };

  const handleTollingFloorUpsideField = (key, value, legacyKey = null) => {
    const nextTollingFloorUpside = {
      ...tollingFloorUpside,
      [key]: value,
      effectiveFloorPreview: null,
    };
    let nextCfg = setPathInObject(cfg, "tollingFloorUpside", nextTollingFloorUpside);
    if (legacyKey) {
      nextCfg = setPathInObject(nextCfg, legacyKey, value);
    }
    commit(nextCfg);
  };

  const handleTollingFloorUpsideNestedField = (path, value) => {
    const nextTollingFloorUpside = setPathInObject(
      {
        ...tollingFloorUpside,
        bankability: { ...(tollingFloorUpside.bankability || {}) },
        includedRevenueStreams: { ...(tollingFloorUpside.includedRevenueStreams || {}) },
        performanceRegime: { ...(tollingFloorUpside.performanceRegime || {}) },
      },
      path,
      value
    );
    nextTollingFloorUpside.effectiveFloorPreview = null;
    commit(setPathInObject(cfg, "tollingFloorUpside", nextTollingFloorUpside));
  };

  const handleTollingFixedCapacityField = (key, value, legacyKey = null) => {
    const nextTollingFixedCapacity = {
      ...tollingFixedCapacity,
      [key]: value,
      fixedCapacityFeePreview: null,
    };
    let nextCfg = setPathInObject(cfg, "tollingFixedCapacity", nextTollingFixedCapacity);
    if (legacyKey) {
      nextCfg = setPathInObject(nextCfg, legacyKey, value);
    }
    commit(nextCfg);
  };

  const handleTollingFixedCapacityNestedField = (path, value) => {
    const nextTollingFixedCapacity = setPathInObject(
      {
        ...tollingFixedCapacity,
        bankability: { ...(tollingFixedCapacity.bankability || {}) },
        includedRevenueStreams: { ...(tollingFixedCapacity.includedRevenueStreams || {}) },
        performanceRegime: { ...(tollingFixedCapacity.performanceRegime || {}) },
      },
      path,
      value
    );
    nextTollingFixedCapacity.fixedCapacityFeePreview = null;
    commit(setPathInObject(cfg, "tollingFixedCapacity", nextTollingFixedCapacity));
  };

  const handleHybridPartialField = (key, value, legacyKey = null) => {
    const nextHybridPartialTolling = {
      ...hybridPartialTolling,
      [key]: value,
      hybridPartialPreview: null,
    };
    if (key === "contractedSharePct") {
      nextHybridPartialTolling.merchantSharePct = 100 - Number(value || 0);
    }
    let nextCfg = setPathInObject(cfg, "hybridPartialTolling", nextHybridPartialTolling);
    if (legacyKey) {
      nextCfg = setPathInObject(nextCfg, legacyKey, value);
    }
    if (key === "contractedSharePct") {
      nextCfg = setPathInObject(nextCfg, "hybridContractedSharePct", value);
      nextCfg = setPathInObject(nextCfg, "hybridMerchantSharePct", 100 - Number(value || 0));
    }
    commit(nextCfg);
  };

  const handleHybridPartialNestedField = (path, value) => {
    const nextHybridPartialTolling = setPathInObject(
      {
        ...hybridPartialTolling,
        bankability: { ...(hybridPartialTolling.bankability || {}) },
        includedRevenueStreams: { ...(hybridPartialTolling.includedRevenueStreams || {}) },
        performanceRegime: { ...(hybridPartialTolling.performanceRegime || {}) },
      },
      path,
      value
    );
    nextHybridPartialTolling.hybridPartialPreview = null;
    commit(setPathInObject(cfg, "hybridPartialTolling", nextHybridPartialTolling));
  };

  const handlePpaField = (key, value) => {
    commit(setPathInObject(cfg, "ppa", { ...ppa, [key]: value }));
  };

  const handlePpaPreset = (presetName) => {
    const nextPpa = applyPpaPreset(presetName, {
      basePrice: ppa.basePrice,
      priceUnit: ppa.priceUnit,
      contractTermYears: ppa.contractTermYears,
      floorPricePerMwh: ppa.floorPricePerMwh,
    });
    nextPpa.preset = presetName;
    commit(setPathInObject(cfg, "ppa", nextPpa));
  };

  const renderPpaSelect = (label, key, options, gridMd = 3, hint = "") => (
    <Grid item xs={12} md={gridMd}>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={ppa[key]}
          onChange={(e) => handlePpaField(key, e.target.value)}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label || o.value}
            </MenuItem>
          ))}
        </Select>
        {hint ? <FormHelperText>{hint}</FormHelperText> : null}
      </FormControl>
    </Grid>
  );

  const renderPpaNumber = (label, key, gridMd = 3, hint = "") => (
    <Grid item xs={12} md={gridMd}>
      <TextField
        fullWidth
        label={label}
        type="number"
        value={ppa[key]}
        onChange={(e) => handlePpaField(key, Number(e.target.value))}
        InputProps={
          hint
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <MaybeTooltip title={hint} placement="top" arrow>
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </MaybeTooltip>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    </Grid>
  );

  // Reusable info "i" end-adornment for TextFields (purely additive hover help).
  const infoIcon = (hint) => (
    <InputAdornment position="end">
      <MaybeTooltip title={hint} placement="top" arrow>
        <IconButton size="small" tabIndex={-1}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </MaybeTooltip>
    </InputAdornment>
  );

  // Auto-filled from the coordinates and overridable, the same contract the
  // bidding zone has: the pin proposes, the user disposes. The stored figure is
  // what the engines run on -- both of them prefer it over the elevation service
  // and skip the lookup entirely when it is present -- because the service is a
  // global digital elevation model and is routinely tens of metres out on a
  // hillside, and a surveyed number should not be thrown away by a run that
  // knows less than the person who typed it.
  //
  // Drift is bounded rather than prevented: moving the pin re-derives the value,
  // so an override survives edits elsewhere on the form but never outlives the
  // site it was entered for.
  const [elevation, setElevation] = useState({ value: null, busy: false, error: "" });
  const [terrain, setTerrain] = useState({ busy: false, error: "" });

  /**
   * The site, from the parcel: its area in square metres and the slope of the
   * ground under it, in one action.
   *
   * Elevations are sampled across the drawn boundary and a plane fitted
   * through them; its tilt and downhill direction land in the two slope
   * fields, and the boundary's own area lands in the terrain area. All three
   * stay editable -- a survey beats a global elevation model -- and the fit
   * is kept on the site so the layout sheet can say how well a plane describes
   * this ground.
   */
  const applyParcel = async () => {
    const parcel = cfg.loc_info?.parcel;
    const ring = ringFromParcel(parcel);
    if (!ring) return;
    setTerrain({ busy: true, error: "" });
    const areaM2 = Math.round(ringAreaM2(ring));
    try {
      const out = await fetchParcelTerrain(parcel, null);
      const plane = out?.plane || {};
      commit({
        ...cfg,
        roof_area: areaM2,
        loc_info: {
          ...(cfg.loc_info || {}),
          tilt: plane.slope_deg ?? cfg.loc_info?.tilt,
          azimuth: plane.slope_azimuth_deg ?? cfg.loc_info?.azimuth,
          terrain: {
            source: out?.source || null,
            plane,
            description: out?.description || null,
            points: Array.isArray(out?.points) ? out.points.length : 0,
            measured_at: new Date().toISOString(),
          },
        },
      });
      setTerrain({ busy: false, error: "" });
    } catch (e) {
      // The area needs no service; keep it even when the elevations fail.
      commit({ ...cfg, roof_area: areaM2 });
      setTerrain({ busy: false, error: e?.message || "The ground could not be measured; the area was applied." });
    }
  };
  const derivedElevationFor = useRef(null);
  useEffect(() => {
    const lat = cfg?.loc_info?.lat;
    const lon = cfg?.loc_info?.lon;
    if (lat == null || lon == null) return undefined;

    // Opening a saved project must not silently redraw over the figure that
    // project was signed off with. Derive only when there is nothing stored, or
    // when THESE coordinates have not been derived for yet in this session --
    // which is what makes an override survive every edit except moving the pin.
    const key = `${lat}:${lon}`;
    const stored = Number(cfg?.loc_info?.elevation_m);
    if (derivedElevationFor.current === key) return undefined;
    if (Number.isFinite(stored) && derivedElevationFor.current === null) {
      derivedElevationFor.current = key;      // adopt what was saved
      setElevation({ value: stored, busy: false, error: "" });
      return undefined;
    }

    let cancelled = false;
    // The elevation service can stall for minutes (it retries an upstream DEM
    // that is often down). The field is optional for a run, so after a while
    // the lookup is given up and the field says it can be typed in, instead of
    // showing «Επίλυση…» for as long as the request hangs.
    const lookup = typeof AbortController === "undefined" ? null : new AbortController();
    let giveUp = null;
    setElevation((e) => ({ ...e, busy: true, error: "" }));
    const timer = setTimeout(async () => {
      if (lookup) giveUp = setTimeout(() => lookup.abort(), ELEVATION_LOOKUP_TIMEOUT_MS);
      try {
        const data = await fetchSiteElevation(lat, lon, null, { signal: lookup?.signal });
        // A response without a usable number is the same outcome as no
        // response: `Number(undefined)` is NaN, and NaN is not null, so
        // without this the field would be filled with NaN.
        const metres = Number(data?.elevation_m);
        if (cancelled) return;
        if (!Number.isFinite(metres)) {
          setElevation({ value: null, busy: false, error: "Elevation lookup unavailable — enter it manually if you have a surveyed figure." });
          return;
        }
        setElevation({ value: metres, busy: false, error: "" });
        derivedElevationFor.current = key;
        // Committed from the ref, not from the `cfg` this effect closed over:
        // the user may have edited another field while the request was in
        // flight, and rebuilding from a stale base would silently revert it.
        const cfgNow = cfgRef.current;
        const rounded = Math.round(metres * 10) / 10;
        if (Number(cfgNow?.loc_info?.elevation_m) !== rounded) {
          history.commit({
            ...cfgNow,
            loc_info: { ...(cfgNow.loc_info || {}), elevation_m: rounded },
          });
        }
      } catch {
        // Not worth interrupting anyone over, and never worth clearing a value
        // the user typed: an unreachable lookup leaves whatever is in the field.
        if (!cancelled) {
          setElevation({ value: null, busy: false, error: "Elevation lookup unavailable — enter it manually if you have a surveyed figure." });
        }
      } finally {
        clearTimeout(giveUp);
      }
    }, 400);                       // the pin can be dragged; wait for it to settle
    return () => { cancelled = true; clearTimeout(timer); clearTimeout(giveUp); lookup?.abort(); };
  }, [cfg?.loc_info?.lat, cfg?.loc_info?.lon, cfg?.loc_info?.elevation_m, history]);

  const downloadTmy = async () => {
    if (!BASE_URL) return;

    setTmyError("");
    setTmyLoading(true);

    try {
      // PVGIS for the coordinates on screen, as a file. Deliberately NOT the
      // project's active dataset: this downloads and changes nothing, so the
      // button can never quietly swap the weather a simulation runs on.
      const lat = Number(cfg?.loc_info?.lat);
      const lon = Number(cfg?.loc_info?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        throw new Error("Set the latitude and longitude first.");
      }

      const { blob, filename } = await downloadPvgisTmy({ lat, lon }, null);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setTmyError(err?.message || "TMY download failed.");
    } finally {
      setTmyLoading(false);
    }
  };

  return (
    // <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <SetupPageHeader
        title="Site & General"
        description={[
          "Name the asset, place it on its site, and set the commercial model it is evaluated against.",
          "The coordinates drive the weather data and market zone; the configuration drives every downstream run — PV System, BESS, Daily Optimization and the Feasibility Study.",
        ]}
        sx={{ mb: 2 }}
      />


      {/* --- PROJECT DETAILS BLOCK --- */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.85rem",
              color: "text.primary",
            }}
          >
            Details
          </Typography>
        </AccordionSummary>

        <AccordionDetails>


          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Asset Name"
                value={activeAsset?.name || ""}
                onChange={(e) => activeAsset && updateAsset(activeAsset.id, { name: e.target.value })}
                InputLabelProps={{
                  sx: {
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": { color: "primary.main" },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <AssetTypeField
                key={activeAsset?.id || "current"}
                config={cfg}
                onCommit={commit}
                windDisabledMessage={windSystemDisabledMessage}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Time Zone"
                value={cfg.timeZone || ""}
                onChange={(e) => setTop("timeZone", e.target.value)}
                placeholder={
                  tzLoading
                    ? "Detecting from coordinates..."
                    : "e.g. Europe/Athens"
                }
                helperText={
                  tzLoading ? (
                    "Detecting timezone from latitude and longitude…"
                  ) : cfg.timeZone ? (
                    <>
                      Automatically detected from coordinates.
                      <br />
                      You can adjust it manually.
                    </>
                  ) : (
                    "Enter a valid IANA timezone (e.g. Europe/Athens)."
                  )
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: tzLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bidding Zone"
                value={cfg.biddingZone || ""}
                onChange={(e) =>
                  setTop("biddingZone", e.target.value.toUpperCase())
                }
                helperText={
                  <>
                    Geographical area where electricity prices,
                    <br />
                    are uniform in the DA/ID markets.
                  </>
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: tzLoading ? (   // or a separate loading flag later
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Address"
                value={cfg.address || ""}
                onChange={(e) =>
                  setTop("address", upperCaseAddress(e.target.value))
                }
                placeholder="e.g. Deutschland, Darmstadt, 64295"
                helperText="Full address of the park. You can adjust it manually."
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: tzLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>


      {/* --- SITE LOCATION BLOCK --- */}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.85rem",
              color: "text.primary",
            }}
          >
            Site location
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/* Row 1: MAP + COORDINATES

              Sized with `size`, not `item`/`xs`. MUI 7's Grid is the former
              Grid2: it does not read xs/md at all, so those props elsewhere in
              this form are inert and the columns are sizing to their content.
              Using the current API is what actually makes this row stack on a
              narrow screen instead of squeezing. */}
          <Grid container spacing={4} alignItems="flex-start" sx={{ mb: 2 }}>
            {/* No heading above the map on purpose: anything here pushes the
                map down while the column beside it starts straight at the
                Latitude field, so the two tops stop lining up. The section is
                already titled, and the caption underneath says what to do. */}
            <Grid size={{ xs: 12, md: 7 }}>
              {/* Writes through setCoords, so the timezone, bidding zone and
                  address lookup fires exactly as it does when the numbers are
                  typed by hand. */}
              <LocationPicker
                lat={cfg.loc_info?.lat}
                lon={cfg.loc_info?.lon}
                onChange={setCoords}
                height={420}
                parcel={ringFromParcel(cfg.loc_info?.parcel, { minPoints: 1 })}
                onApplyParcel={applyParcel}
                applyBusy={terrain.busy}
                applyStatus={
                  terrain.error
                    ? terrain.error
                    : cfg.loc_info?.terrain?.description
                      ? `${cfg.loc_info.terrain.description} (${cfg.loc_info.terrain.points} points sampled.)`
                      : ""
                }
                applyError={Boolean(terrain.error)}
                onParcelChange={(ring) => {
                  // Stored as GeoJSON on the site, beside the coordinates it
                  // encloses. Clearing the last corner clears the parcel; a
                  // one- or two-corner trace is kept so drawing survives a
                  // re-render, but only three or more is a polygon downstream.
                  setLoc("parcel", ring ? (ring.length >= 3 ? parcelFromRing(ring)
                                                             : { type: "Polygon", coordinates: [ring] })
                                        : null);
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={3}>
              <TextField
                fullWidth
                label="Latitude (°)"
                type="number"
                value={cfg.loc_info.lat}
                onChange={(e) => setLoc("lat", Number(e.target.value))}
                helperText={
                  <>
                    Positive = North hemisphere
                    <br />
                    Negative = South hemisphere
                  </>
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MaybeTooltip title="Lines of latitude run east–west." arrow>
                        <IconButton size="small" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Longitude (°)"
                type="number"
                value={cfg.loc_info.lon}
                onChange={(e) => setLoc("lon", Number(e.target.value))}
                helperText={
                  <>
                    Positive = East hemisphere
                    <br />
                    Negative = West hemisphere
                  </>
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MaybeTooltip title="Lines of longitude run north–south." arrow>
                        <IconButton size="small" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Altitude, auto-filled from the coordinates and editable.
                  Labelled "Altitude" on screen; the config key, the API and the
                  engines all still say "elevation", which is the term pvlib and
                  the elevation service use. One rename in the label only, so a
                  saved project does not have to be migrated.
                  It is a real model input, not a label: it sets air pressure
                  through alt2pres and from there the absolute air mass the
                  spectral correction is evaluated on. Being 600 m out moves that
                  correction about 0.27%. Blank means "resolve it at run time",
                  which is what every project did before this field existed. */}
              <TextField
                fullWidth
                label="Altitude (m)"
                type="number"
                value={cfg.loc_info?.elevation_m ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  setLoc("elevation_m", raw === "" ? "" : Number(raw));
                }}
                helperText={
                  elevation.error
                    ? elevation.error
                    : elevation.busy
                      ? "Resolving from the coordinates…"
                      : (
                        <>
                          Metres above sea level. Auto-filled when the pin moves.
                          <br />
                          Overwrite it with a surveyed figure if you have one.
                        </>
                      )
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": { color: "primary.main" },
                  },
                }}
                InputProps={{
                  endAdornment: elevation.busy ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <MaybeTooltip
                        title="Sets site air pressure and the absolute air mass the spectral correction runs on. Auto-filled from a global elevation model, which can be tens of metres out on a slope — override it with a survey when you have one."
                        arrow
                      >
                        <IconButton size="small" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Button
                variant="outlined"
                onClick={downloadTmy}
                disabled={tmyLoading || !cfg.loc_info?.lat || !cfg.loc_info?.lon}
                startIcon={tmyLoading ? <CircularProgress size={16} /> : null}
                sx={{ alignSelf: "flex-start" }}
              >
                {tmyLoading ? "Fetching from PVGIS…" : "Download PVGIS TMY"}
              </Button>

              <MaybeTooltip
                title="Downloads a PVGIS typical meteorological year for these coordinates. Downloading does not change the selected weather source. To use this dataset for PV generation and the feasibility study, import and activate it in Feasibility → Weather Data."
                arrow
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75, display: "inline-block", cursor: "help" }}
                >
                  PVGIS TMY download only. Select the weather used for generation in Feasibility → Weather Data.
                </Typography>
                </MaybeTooltip>
              </Box>

                {tmyError && <Alert severity="error">{tmyError}</Alert>}
              </Stack>
            </Grid>
          </Grid>

          {/* Row 2: TERRAIN -- four equal columns on the current Grid API.
              The legacy item/xs props size nothing on MUI 7, which is why
              this row used to arrange itself by content width. */}
          {!isStandaloneBess && (
          <Grid container spacing={3} alignItems="flex-start" sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                id="field-site-area"
                label="Terrain Area (m²)"
                type="number"
                value={cfg.roof_area}
                onChange={(e) =>
                  handleField("roof_area", Number(e.target.value))
                }
                helperText={
                  ringFromParcel(cfg.loc_info?.parcel)
                    ? "Site area. Apply parcel on the map fills it from the drawn boundary."
                    : "Square metres of the installation site"
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Terrain Slope Tilt (deg)"
                type="number"
                value={cfg.loc_info.tilt}
                onChange={(e) => setLoc("tilt", Number(e.target.value))}
                helperText={
                  cfg.loc_info?.terrain?.plane
                    ? `Measured from the parcel: ${Number(cfg.loc_info.terrain.plane.slope_deg).toFixed(1)}° falling towards ${Math.round(cfg.loc_info.terrain.plane.slope_azimuth_deg)}°`
                    : "Slope of the ground, not panel tilt"
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MaybeTooltip
                        title="Tilt of the land where the system will be installed. A flat site has 0° tilt."
                        placement="top"
                        arrow
                      >
                        <IconButton size="small" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Terrain Slope Azimuth (deg)"
                type="number"
                value={cfg.loc_info.azimuth}
                onChange={(e) => setLoc("azimuth", Number(e.target.value))}
                helperText={
                  <>
                    0° = North, 90° = East
                    <br />
                    180° = South, 270° = West
                  </>
                }
                InputLabelProps={{
                  sx: {
                    color: "primary.main",           // normal state
                    fontWeight: 600,
                    letterSpacing: "0.02em",

                    "&.Mui-focused": {
                      color: "primary.main",         // when focused
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MaybeTooltip
                        title="Direction the ground slopes toward (where water would flow downhill)."
                        placement="top"
                        arrow
                      >
                        <IconButton size="small" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MaybeTooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth id="field-site-surface">
                <InputLabel
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  Surface Type
                </InputLabel>
                <Select
                  label="Surface Type"
                  value={cfg.surfaceType}
                  onChange={(e) => setTop("surfaceType", e.target.value)}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                      sx: {
                        maxHeight: 320,
                      },
                    },
                  }}
                >
                  {SURFACE_OPTIONS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Sets the ground reflectance the model uses.
                </FormHelperText>
              </FormControl>

              <Box sx={{ mt: 1 }}>
                <MonthlyValueGrid
                  label="Albedo"
                  values={cfg.albedoMonthly}
                  onChange={(next) => setTop("albedoMonthly", next)}
                  seedValue={SURFACE_ALBEDOS[cfg.surfaceType] ?? 0.2}
                  seedLabel="surface type"
                  step={0.01}
                  min={0}
                  max={1}
                  decimals={2}
                  helper="Worth setting where the ground holds snow, and on a bifacial plant — it drives the rear irradiance directly."
                  reference={ALBEDO_REFERENCE}
                />
              </Box>
            </Grid>
          </Grid>
          )}
        </AccordionDetails>
      </Accordion>


      {/* --- PROJECT CONFIG BLOCK --- */}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.85rem",
              color: "text.primary",
            }}
          >
            Configuration
          </Typography>
        </AccordionSummary>

        <AccordionDetails>

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  alignItems: "flex-start",
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  width: "100%",
                }}
              >

                <Box
                  sx={{
                    flex: { xs: "1 1 100%", md: "0 0 340px" },
                    minWidth: 0,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      }}
                    >
                      Revenue Model
                    </InputLabel>
                    <Select
                      label="Revenue Model"
                      value={cfg.settlementType}
                      onChange={(e) => {
                        if (!getRevenueModelDisabledMessage(demoProfile, e.target.value)) {
                          // Entering Behind-the-Meter sets the BESS-page grid
                          // switches it implies in the same step.
                          if (e.target.value === "BTM") {
                            commit(alignBtmSwitches(enableDemandForBtmSelection(setPathInObject(cfg, "settlementType", "BTM"))));
                          } else {
                            handleField("settlementType", e.target.value);
                          }
                        }
                      }}
                    >
                      <MenuItem value="Arbitrage" disabled={Boolean(merchantDisabledMessage) || assetTypeDefinition(cfg.systemType)?.value === "Standalone PV"}>
                        Merchant / Trading (Arbitrage)
                      </MenuItem>
                      <MenuItem value="PPA" disabled={Boolean(ppaDisabledMessage) || assetTypeDefinition(cfg.systemType)?.value === "Standalone PV"}>
                        PPA (Power Purchase Agreement)
                      </MenuItem>
                      <MenuItem value="TollingFixed" disabled={Boolean(tollingDisabledMessage) || assetTypeDefinition(cfg.systemType)?.value === "Standalone PV"}>Tolling - Fixed Capacity</MenuItem>
                      <MenuItem value="TollingFloorUpside" disabled={Boolean(tollingDisabledMessage) || assetTypeDefinition(cfg.systemType)?.value === "Standalone PV"}>Tolling - Floor + Upside</MenuItem>
                      <MenuItem value="HybridPartial" disabled={Boolean(tollingDisabledMessage) || assetTypeDefinition(cfg.systemType)?.value === "Standalone PV"}>Hybrid - Partial Tolling</MenuItem>
                      <MenuItem value="BTM">
                        Behind-the-Meter - Self-Consumption &amp; Peak Shaving
                      </MenuItem>
                    </Select>
                    {assetTypeDefinition(cfg.systemType)?.value === "Standalone PV" && (
                      <Alert severity={studyAssetIssue(cfg) ? "warning" : "info"} sx={{ mt: 1 }}>
                        {isGreek
                          ? "Οι μελέτες μόνο Φ/Β υποστηρίζονται προς το παρόν μόνο πίσω από τον μετρητή (BTM). Επιλέξτε Πίσω από τον μετρητή· τα υπόλοιπα μοντέλα εσόδων δεν υποστηρίζονται ακόμη χωρίς μπαταρία."
                          : "Standalone PV studies currently support Behind-the-Meter only. Select Behind-the-Meter; other revenue models are not yet supported without a battery."}
                      </Alert>
                    )}
                    <FormHelperText>
                      Defines how this asset earns revenue or saves electricity costs in feasibility calculations. Configure the selected model below; market participation is set separately.
                    </FormHelperText>
                    {selectedRevenueModelDisabledMessage ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {selectedRevenueModelDisabledMessage} Select an enabled revenue model to run a feasibility study, or ask an administrator to enable this model.
                      </Alert>
                    ) : null}
                  </FormControl>
                </Box>
                <Box sx={{ flex: "1 1 0", minWidth: 0, width: { xs: "100%", md: "auto" } }}>
                  <Accordion
                    elevation={0}
                    disableGutters
                    sx={{
                      mt: 0,
                      backgroundColor: "transparent",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, justifyContent: "flex-start", gap: 0.75, "& .MuiAccordionSummary-content": { flexGrow: 0 } }}>
                      <Typography variant="body2">Learn more</Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        px: 0,
                        pt: 0,
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.default",
                          p: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Merchant / Trading (Arbitrage):</b> The battery charges during low-price periods and discharges during high-price periods
                          across Day-Ahead, Intraday, or balancing markets. Revenue is fully merchant-exposed and depends on market spreads.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>PPA:</b> Energy is sold under a contracted tariff, typically for PV or hybrid systems, creating more stable and predictable
                          revenues than pure merchant exposure.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Tolling - Fixed Capacity:</b> A trader or offtaker pays a fixed availability / capacity fee (typically EUR/MW-year) in exchange
                          for dispatch rights or reserved battery capacity. This is one of the most bankable structures for stand-alone BESS.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Tolling - Floor + Upside:</b> The project receives a guaranteed minimum annual revenue floor, plus a share of upside if market
                          revenues exceed that floor. This creates downside protection while preserving some upside.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Hybrid - Partial Tolling:</b> Hybrid Partial Tolling splits the project into a contracted share and a merchant share. The contracted share receives a fixed tolling/capacity fee, while the merchant share remains exposed to merchant market revenue. This differs from Floor + Upside, where the whole project is protected by a revenue floor and only upside above the floor is shared.
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.95 }}>
                          <b>Behind-the-Meter - Self-Consumption &amp; Peak Shaving:</b> The system sits on the customer side of the meter and serves the site's own
                          consumption. There is no market revenue at all: the return is electricity the site no longer buys, so the deliverable is a savings report
                          rather than a bankability report. Savings come from consuming generation on site, from reducing the metered demand peak that drives the
                          capacity charge, and optionally from shifting grid purchases into the off-peak band. Requires a demand profile and the monthly retail
                          tariffs on the Feasibility &gt; Technical page.
                        </Typography>
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                            Structure comparison
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}>
                            <b>Tolling - Floor + Upside:</b> Project receives a minimum revenue floor across the full asset plus a share of upside above the floor. Trader/offtaker covers shortfall below the floor and keeps residual upside.
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}>
                            <b>Tolling - Fixed Capacity:</b> Project receives a fixed capacity/tolling fee during the contract. Trader/offtaker receives or controls merchant value and pays the fixed fee.
                          </Typography>
                          <Typography variant="body2">
                            <b>Hybrid - Partial Tolling:</b> Project receives a fixed fee on the contracted share and keeps merchant revenue on the merchant share. Trader/offtaker receives merchant value from the contracted share and pays the contracted fee.
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>


              </Box>
            </Grid>
          </Grid>




          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* ---------------- PPA ---------------- */}
            {cfg.settlementType === "PPA" && (
              <>
                {/* Preset picker */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>PPA Preset</InputLabel>
                    <Select
                      label="PPA Preset"
                      value={ppa.preset || ""}
                      onChange={(e) => handlePpaPreset(e.target.value)}
                    >
                      {PPA_PRESET_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Prefills the PPA dimensions with a named template; you can still adjust any field afterward.
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {/* Common fields */}
                {renderPpaNumber("Base Price", "basePrice", 3, "Contracted PPA price applied to generation, before shaping, escalation or protection.")}
                {renderPpaSelect("Price Unit", "priceUnit", PPA_PRICE_UNIT_OPTIONS, 2, "Currency unit for the PPA price (EUR/MWh or EUR/kWh).")}
                {renderPpaNumber("Contract Term (years)", "contractTermYears", 3, "Number of years the PPA price applies; afterwards the project follows the post-contract mode.")}
                {["floor", "collar", "floor_plus_upside_share"].includes(
                  ppa.priceProtection
                ) && renderPpaNumber("Floor Price", "floorPricePerMwh", 3, "Guaranteed minimum price; the project earns at least this per MWh.")}

                <Grid item xs={12}>
                  <MaybeTooltip
                    title="Adds a fair-price (price-discovery) analysis section to the report; it does not change the revenue calculation."
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ppa.showFairPrice !== false}
                          onChange={(e) =>
                            handlePpaField("showFairPrice", e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label="Show price-discovery (fair-price) analysis in the report"
                    />
                  </MaybeTooltip>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.2 }}>
                      PPA
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {PPA_HELPER_TEXT}
                    </Typography>
                  </Box>
                </Grid>

                {/* Advanced dimensions */}
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                      <Typography variant="subtitle2">
                        Advanced PPA settings
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Grid container spacing={2}>
                        {renderPpaSelect("Settlement structure", "settlementStructure", PPA_SETTLEMENT_STRUCTURE_OPTIONS, 4, "Physical delivery of energy versus a financial CfD settled against a reference index.")}
                        {ppa.settlementStructure === "financial_cfd" &&
                          renderPpaSelect("CfD direction", "cfdDirection", PPA_CFD_DIRECTION_OPTIONS, 4, "Two-way swap settles both above and below the strike; one-way floor pays only when the market is below strike.")}
                        {renderPpaSelect("Volume basis", "volumeBasis", PPA_VOLUME_BASIS_OPTIONS, 4, "How the contracted volume is defined - as-produced, baseload block, 24x7 matched, percent of generation, or firm block.")}
                        {ppa.volumeBasis === "percent_of_generation" &&
                          renderPpaNumber("Contracted share (%)", "contractedSharePct", 3, "Percentage of generation sold under the PPA; the remainder stays merchant.")}
                        {renderPpaSelect("Price shape", "priceShape", PPA_PRICE_SHAPE_OPTIONS, 4, "Whether the PPA price is flat, time-of-day shaped, or indexed/escalated.")}
                        {renderPpaSelect("Price protection", "priceProtection", PPA_PRICE_PROTECTION_OPTIONS, 4, "Optional floor, collar, or floor-plus-upside protection applied to the PPA price.")}
                        {ppa.priceProtection === "collar" &&
                          renderPpaNumber("Cap price", "capPricePerMwh", 3, "Upper price bound under a collar; value above this is shared with the offtaker.")}
                        {ppa.priceProtection === "floor_plus_upside_share" &&
                          renderPpaNumber("Project upside share (%)", "projectUpsideSharePct", 3, "Share of above-floor merchant upside the project keeps under floor-plus-upside protection.")}
                        {renderPpaSelect("Escalation", "escalationMode", PPA_ESCALATION_MODE_OPTIONS, 4, "How the PPA price grows over time - flat, fixed %, CPI-linked, or a custom curve.")}
                        {ppa.escalationMode !== "none" &&
                          renderPpaNumber("Escalation (%/yr)", "annualEscalationPct", 3, "Annual percentage by which the PPA price escalates each year.")}
                        {renderPpaSelect("Capture rate mode", "captureRateMode", PPA_CAPTURE_RATE_MODE_OPTIONS, 4, "How the capture factor is applied to merchant-valued energy - flat, annual decline, or custom curve.")}
                        {renderPpaNumber("Capture rate (0-1)", "captureRate", 3, "Fraction of the average market price that generation actually captures (e.g. 0.6 = 60%).")}
                        {renderPpaSelect("Negative-price rule", "negativePriceRule", PPA_NEGATIVE_PRICE_RULE_OPTIONS, 4, "Behaviour during negative prices - curtail for zero revenue, deliver anyway, or suspend settlement.")}
                        {renderPpaNumber("Imbalance cost (EUR/MWh)", "imbalanceCostPerMwh", 3, "Balancing / BRP cost per MWh deducted from merchant-exposed volume.")}
                        {renderPpaSelect("Post-contract mode", "postContractRevenueMode", PPA_POST_CONTRACT_MODE_OPTIONS, 4, "Revenue assumption after the PPA term ends - merchant reversion, zero, or continue the PPA price.")}
                        {renderPpaSelect("Merchant residual", "merchantResidualTreatment", PPA_TREATMENT_OPTIONS, 4, "Whether uncontracted merchant revenue enters the bankable base case or is shown as disclosure-only upside.")}
                        {renderPpaSelect("Post-PPA tail", "postContractTailTreatment", PPA_TREATMENT_OPTIONS, 4, "Whether post-contract tail revenue enters the bankable base case or is disclosure-only upside.")}
                        {renderPpaSelect("Above-floor upside", "aboveFloorUpsideTreatment", PPA_TREATMENT_OPTIONS, 4, "Whether above-floor upside enters the bankable base case or is shown as disclosure-only upside.")}
                        {renderPpaNumber("Merchant haircut (%)", "merchantHaircutPct", 3, "Conservative percentage reduction applied to merchant-valued revenue for bankability.")}
                        {renderPpaSelect("Applies to", "appliesTo", PPA_APPLIES_TO_OPTIONS, 4, "Which assets the PPA covers - PV only, PV + BESS, or standalone BESS.")}
                        {renderPpaSelect("Yield for debt", "yieldProbabilityForDebt", PPA_YIELD_PROBABILITY_OPTIONS, 4, "Disclosure only in the current engine: debt is sized on the P50 (base-case) CFADS. This selection is recorded for lender discussion; it does not yet change debt sizing.")}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </>
            )}

            {/* ---------------- Merchant / Trading (Arbitrage) ---------------- */}
            {cfg.settlementType === "Arbitrage" && (
              <Grid item xs={12}>
                {regulatoryFramework.mode === "11A" ? (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Market Revenue Stack
                    </Typography>
                    <Alert severity="info">
                      Locked to <b>Day-Ahead (DAM)</b> by regulatory Category 11A: PV energy is
                      remunerated at the fixed Reference Price and battery energy at the DAM
                      price — no other market can be part of the 11A energy settlement. Switch
                      the regulatory framework to &quot;None&quot; or &quot;11B&quot; to select
                      additional markets.
                    </Alert>
                  </Box>
                ) : (
                  renderMarketRevenueStack(
                    cfg.arbitrageMerchant?.includedRevenueStreams ?? { dam: true },
                    (key, checked, nextStreams) =>
                      nextStreams
                        ? handleField("arbitrageMerchant.includedRevenueStreams", nextStreams)
                        : handleField(`arbitrageMerchant.includedRevenueStreams.${key}`, checked),
                    "arbitrage",
                    isProjectGR,
                    cfg.arbitrageMerchant?.balancingV1,
                    (nextConfig) => handleField("arbitrageMerchant.balancingV1", nextConfig),
                    installedBessPowerMw,
                    cfg.biddingZone,
                    cfg.arbitrageMerchant?.capacityPayments,
                    (nextConfig) => handleField("arbitrageMerchant.capacityPayments", nextConfig),
                    cfg.arbitrageMerchant?.guaranteesOfOrigin,
                    (nextConfig) => handleField("arbitrageMerchant.guaranteesOfOrigin", nextConfig),
                    regulatoryFramework.mode,
                    stationContext,
                    isGreek
                  )
                )}
                <RegulatoryFrameworkSection
                  value={regulatoryFramework}
                  onChange={handleRegulatoryFrameworkChange}
                  connectionCapacityKw={Number(cfg.grid?.exportLimitKw) || 0}
                  pvInstalledKw={Number(cfg.pvInstalledCap?.kw) || 0}
                  bessPowerKw={(Number(installedBessPowerMw) || 0) * 1000}
                  isStandaloneBess={isStandaloneBess}
                />
              </Grid>
            )}

            {/* ------- Connection & curtailment (all front-of-meter models) ------- */}
            {cfg.settlementType !== "BTM" && (
              <Grid item xs={12}>
                <ConnectionCurtailmentSection
                  value={connectionCurtailment}
                  onChange={handleConnectionCurtailmentChange}
                  settlementType={cfg.settlementType}
                  regulatoryCapEnabled={Boolean(regulatoryFramework?.injectionCap?.enabled) &&
                    ["11A", "11B"].includes(String(regulatoryFramework?.mode || ""))}
                />
              </Grid>
            )}

            {/* ---------------- Behind-the-Meter ---------------- */}
            {cfg.settlementType === "BTM" && (
              <Grid item xs={12}>
                <BtmSection
                  value={btm}
                  onChange={handleBtmChange}
                  cfg={cfg}
                  isStandaloneBess={isStandaloneBess}
                />
              </Grid>
            )}

            {/* ---------------- Tolling: Fixed Capacity ---------------- */}
            {cfg.settlementType === "TollingFixed" && (
              <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Fee Input Mode</InputLabel>
                      <Select
                        label="Fee Input Mode"
                        value={tollingFixedCapacity.feeInputMode}
                        onChange={(e) =>
                          handleTollingFixedCapacityField("feeInputMode", e.target.value)
                        }
                      >
                        {FIXED_CAPACITY_FEE_INPUT_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select whether the fixed fee is capacity-based or entered as an absolute amount.
                      </FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label={getFixedCapacityFeeAmountLabel(tollingFixedCapacity.feeInputMode)}
                      type="number"
                      value={tollingFixedCapacity.feeAmount ?? ""}
                      onChange={(e) =>
                        handleTollingFixedCapacityField(
                          "feeAmount",
                          e.target.value === "" ? null : Number(e.target.value),
                          "tollingFixedFeePerMWYear"
                        )
                      }
                      error={Boolean(fixedCapacityValidation.feeAmount)}
                      helperText={
                        fixedCapacityValidation.feeAmount ||
                        "Gross fixed fee before availability adjustment and penalties."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Gross fixed capacity/tolling fee in the selected unit, before availability adjustment and penalties."
                        ),
                      }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        label="Settlement Frequency"
                        value={tollingFixedCapacity.settlementFrequency}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "settlementFrequency",
                            e.target.value,
                            "tollingSettlementFrequency"
                          )
                        }
                      >
                        {FIXED_CAPACITY_SETTLEMENT_FREQUENCIES.map((frequency) => (
                          <MenuItem key={frequency.value} value={frequency.value}>
                            {frequency.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Period used for invoicing and preview values.</FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Contract Term (years)"
                      type="number"
                      value={tollingFixedCapacity.contractTermYears}
                      onChange={(e) =>
                        handleTollingFixedCapacityField(
                          "contractTermYears",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingContractYears"
                        )
                      }
                      error={Boolean(fixedCapacityValidation.contractTerm)}
                      helperText={
                        fixedCapacityValidation.contractTerm ||
                        "Contracted duration for the fixed capacity arrangement."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Number of years the fixed capacity fee is contracted; after this the project follows the post-contract revenue mode."
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    {isAbsoluteFixedFeeMode ? (
                      <Alert
                        severity="info"
                        sx={{ gridColumn: { xs: "1", md: "span 2" }, alignItems: "center" }}
                      >
                        Capacity is not used to calculate this explicit fixed fee.
                      </Alert>
                    ) : (
                      <>
                        <FormControl fullWidth>
                          <InputLabel>Capacity Basis</InputLabel>
                          <Select
                            label="Capacity Basis"
                            value={tollingFixedCapacity.capacityBasis}
                            onChange={(e) =>
                              handleTollingFixedCapacityField(
                                "capacityBasis",
                                e.target.value,
                                "tollingCapacityBasis"
                              )
                            }
                          >
                            <MenuItem value="installed_power_mw">Installed Power (MW)</MenuItem>
                            <MenuItem value="contracted_capacity_mw">
                              Contracted Capacity (MW)
                            </MenuItem>
                          </Select>
                          <FormHelperText>
                            Capacity used to derive the fixed fee when the fee is capacity-based.
                          </FormHelperText>
                        </FormControl>

                        {tollingFixedCapacity.capacityBasis === "contracted_capacity_mw" ? (
                          <TextField
                            fullWidth
                            label="Contracted Capacity (MW)"
                            type="number"
                            value={tollingFixedCapacity.contractedCapacityMw ?? ""}
                            onChange={(e) =>
                              handleTollingFixedCapacityField(
                                "contractedCapacityMw",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(fixedCapacityValidation.contractedCapacity)}
                            helperText={
                              fixedCapacityValidation.contractedCapacity ||
                              fixedCapacityValidation.contractedCapacityWarning ||
                              "Editable only when contracted capacity is selected."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Battery MW the fee is calculated on when Capacity Basis is set to contracted capacity."
                              ),
                            }}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            label="Installed BESS Power (MW)"
                            value={
                              installedBessPowerMw == null ? "Not available" : installedBessPowerMw
                            }
                            InputProps={{
                              readOnly: true,
                              endAdornment: infoIcon(
                                "Total installed BESS discharge power derived from the selected battery equipment; used as the capacity basis."
                              ),
                            }}
                            helperText="Derived from selected BESS equipment."
                          />
                        )}
                      </>
                    )}

                    <TextField
                      fullWidth
                      label="Availability Adjustment (%)"
                      type="number"
                      value={tollingFixedCapacity.availabilityAdjustmentPct ?? ""}
                      onChange={(e) =>
                        handleTollingFixedCapacityField(
                          "availabilityAdjustmentPct",
                          e.target.value === "" ? null : Number(e.target.value),
                          "tollingAvailabilityPct"
                        )
                      }
                      error={Boolean(fixedCapacityValidation.availability)}
                      helperText={
                        fixedCapacityValidation.availability ||
                        "Applied to gross fixed fee when enabled."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Percentage of the fixed fee actually earned after expected unavailability; applied only when the adjustment switch is on."
                        ),
                      }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 56 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(tollingFixedCapacity.applyAvailabilityAdjustment)}
                            onChange={(e) =>
                              handleTollingFixedCapacityField(
                                "applyAvailabilityAdjustment",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <MaybeTooltip
                            title="When on, the fixed fee is scaled by the Availability Adjustment percentage."
                            placement="top"
                            arrow
                          >
                            <span>Apply availability adjustment</span>
                          </MaybeTooltip>
                        }
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Annual Escalation (%)"
                      type="number"
                      value={tollingFixedCapacity.annualEscalationPct}
                      onChange={(e) =>
                        handleTollingFixedCapacityField(
                          "annualEscalationPct",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingEscalationPct"
                        )
                      }
                      helperText="Optional annual increase in the contracted fixed fee."
                      InputProps={{
                        endAdornment: infoIcon(
                          "Optional yearly percentage increase applied to the fixed fee over the contract term."
                        ),
                      }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Penalty Mode</InputLabel>
                      <Select
                        label="Penalty Mode"
                        value={tollingFixedCapacity.penaltyMode}
                        onChange={(e) =>
                          handleTollingFixedCapacityField("penaltyMode", e.target.value)
                        }
                      >
                        {FIXED_CAPACITY_PENALTY_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Optional deduction from the fixed fee.</FormHelperText>
                    </FormControl>

                    {tollingFixedCapacity.penaltyMode === "fixed_annual_eur" && (
                      <TextField
                        fullWidth
                        label="Annual Penalty (EUR/year)"
                        type="number"
                        value={tollingFixedCapacity.annualPenaltyEur ?? ""}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "annualPenaltyEur",
                            e.target.value === "" ? null : Number(e.target.value),
                            "tollingPenaltyAnnual"
                          )
                        }
                        error={Boolean(fixedCapacityValidation.annualPenalty)}
                        helperText={
                          fixedCapacityValidation.annualPenalty ||
                          "Fixed annual deduction from the availability-adjusted fee."
                        }
                        InputProps={{
                          endAdornment: infoIcon(
                            "Fixed euro amount deducted from the availability-adjusted fee each year."
                          ),
                        }}
                      />
                    )}

                    {tollingFixedCapacity.penaltyMode === "percent_of_gross_fee" && (
                      <TextField
                        fullWidth
                        label="Penalty (% of gross fee)"
                        type="number"
                        value={tollingFixedCapacity.penaltyPctOfGrossFee ?? ""}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "penaltyPctOfGrossFee",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(fixedCapacityValidation.penaltyPct)}
                        helperText={
                          fixedCapacityValidation.penaltyPct ||
                          "Annual penalty calculated as a share of gross fixed fee."
                        }
                        InputProps={{
                          endAdornment: infoIcon(
                            "Annual penalty expressed as a percentage of the gross fixed fee."
                          ),
                        }}
                      />
                    )}

                    {tollingFixedCapacity.penaltyMode === "fixed_per_settlement_period" && (
                      <TextField
                        fullWidth
                        label="Penalty (EUR/settlement period)"
                        type="number"
                        value={tollingFixedCapacity.penaltyPerSettlementPeriodEur ?? ""}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "penaltyPerSettlementPeriodEur",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(fixedCapacityValidation.periodPenalty)}
                        helperText={
                          fixedCapacityValidation.periodPenalty ||
                          "Deduction applied once per settlement period."
                        }
                        InputProps={{
                          endAdornment: infoIcon(
                            "Fixed euro penalty deducted once per settlement period."
                          ),
                        }}
                      />
                    )}

                    {tollingFixedCapacity.penaltyMode === "none" && (
                      <Alert severity="info" sx={{ alignItems: "center" }}>
                        No fixed-fee penalty is applied.
                      </Alert>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Merchant Optimization</InputLabel>
                      <Select
                        label="Merchant Optimization"
                        value={tollingFixedCapacity.merchantOptimization ? "Yes" : "No"}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "merchantOptimization",
                            e.target.value === "Yes",
                            "useMerchantOptimization"
                          )
                        }
                      >
                        <MenuItem value="Yes">Enabled</MenuItem>
                        <MenuItem value="No">Disabled</MenuItem>
                      </Select>
                      <FormHelperText>
                        If enabled, merchant dispatch value is used for settlement and trader/offtaker economics.
                      </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Merchant Revenue Recipient</InputLabel>
                      <Select
                        label="Merchant Revenue Recipient"
                        value={tollingFixedCapacity.merchantRevenueRecipient}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "merchantRevenueRecipient",
                            e.target.value
                          )
                        }
                      >
                        {MERCHANT_REVENUE_RECIPIENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Party entitled to merchant value during the tolling term.</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Post-contract Revenue Mode</InputLabel>
                      <Select
                        label="Post-contract Revenue Mode"
                        value={tollingFixedCapacity.postContractRevenueMode}
                        onChange={(e) =>
                          handleTollingFixedCapacityField(
                            "postContractRevenueMode",
                            e.target.value
                          )
                        }
                      >
                        {POST_CONTRACT_REVENUE_MODE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Revenue assumption after the fixed-fee term.</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Merchant Value Basis</InputLabel>
                      <Select
                        label="Merchant Value Basis"
                        value={tollingFixedCapacity.merchantValueBasis}
                        onChange={(e) =>
                          handleTollingFixedCapacityField("merchantValueBasis", e.target.value)
                        }
                      >
                        {MERCHANT_VALUE_BASIS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>How merchant value is interpreted for tolling economics.</FormHelperText>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {renderMarketRevenueStack(
                    tollingFixedCapacity.includedRevenueStreams,
                    (key, checked, nextStreams) =>
                      handleTollingFixedCapacityNestedField(
                        nextStreams ? "includedRevenueStreams" : `includedRevenueStreams.${key}`,
                        nextStreams || checked
                      ),
                    "fixed_capacity",
                    isProjectGR,
                    tollingFixedCapacity.balancingV1,
                    (nextConfig) => handleTollingFixedCapacityNestedField("balancingV1", nextConfig),
                    installedBessPowerMw,
                    cfg.biddingZone,
                    tollingFixedCapacity.capacityPayments,
                    (nextConfig) => handleTollingFixedCapacityNestedField("capacityPayments", nextConfig),
                    tollingFixedCapacity.guaranteesOfOrigin,
                    (nextConfig) => handleTollingFixedCapacityNestedField("guaranteesOfOrigin", nextConfig),
                    regulatoryFramework.mode,
                    stationContext,
                    isGreek
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Effective Fixed Fee Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      This preview is calculated from the visible fixed capacity inputs. The backend recalculates the fixed fee from the raw inputs during feasibility and export.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        label={`Gross fixed fee/${getFixedCapacitySettlementPeriodLabel(tollingFixedPreview.settlementFrequency)}`}
                        value={formatCurrency(tollingFixedPreview.grossFeePerSettlementPeriod)}
                        InputProps={{ readOnly: true }}
                        helperText="Before availability adjustment and penalties."
                      />
                      <TextField
                        fullWidth
                        label="Availability Adjustment"
                        value={
                          tollingFixedCapacity.applyAvailabilityAdjustment
                            ? formatPercent(tollingFixedPreview.availabilityPct)
                            : "Not applied"
                        }
                        InputProps={{ readOnly: true }}
                        helperText="Applied to gross fixed fee when enabled."
                      />
                      <TextField
                        fullWidth
                        label={`Penalty/${getFixedCapacitySettlementPeriodLabel(tollingFixedPreview.settlementFrequency)}`}
                        value={formatCurrency(tollingFixedPreview.penaltyPerSettlementPeriod)}
                        InputProps={{ readOnly: true }}
                        helperText="Deduction after availability adjustment."
                      />
                      <TextField
                        fullWidth
                        label={`Net fixed fee/${getFixedCapacitySettlementPeriodLabel(tollingFixedPreview.settlementFrequency)}`}
                        value={formatCurrency(tollingFixedPreview.netFeePerSettlementPeriod)}
                        InputProps={{ readOnly: true }}
                        helperText="Effective fixed fee for settlement."
                      />
                      <TextField
                        fullWidth
                        label="Annualized Gross Fixed Fee"
                        value={formatCurrency(tollingFixedPreview.annualGrossFee)}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        fullWidth
                        label="Annualized Net Fixed Fee"
                        value={formatCurrency(tollingFixedPreview.annualNetFixedFee)}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        fullWidth
                        label="Capacity Used"
                        value={
                          isAbsoluteFixedFeeMode
                            ? "Not used"
                            : `${Number(tollingFixedPreview.capacityMw || 0).toLocaleString("en-US", {
                                maximumFractionDigits: 3,
                              })} MW`
                        }
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Best use case
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited for fully contracted, bankable BESS cases.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Bankability & Contract Assumptions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                          gap: 3,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Counterparty Status</InputLabel>
                          <Select
                            label="Counterparty Status"
                            value={tollingFixedCapacity.bankability?.counterpartyStatus}
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "bankability.counterpartyStatus",
                                e.target.value
                              )
                            }
                          >
                            {COUNTERPARTY_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            How firm the offtaker is; a signed counterparty is more bankable than a generic assumption.
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>Counterparty Type</InputLabel>
                          <Select
                            label="Counterparty Type"
                            value={tollingFixedCapacity.bankability?.counterpartyType}
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "bankability.counterpartyType",
                                e.target.value
                              )
                            }
                          >
                            {COUNTERPARTY_TYPE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Category of the offtaker (utility, trader, corporate, etc.) used for bankability context.
                          </FormHelperText>
                        </FormControl>

                        <TextField
                          fullWidth
                          label="Counterparty Name"
                          value={tollingFixedCapacity.bankability?.counterpartyName ?? ""}
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "bankability.counterpartyName",
                              e.target.value
                            )
                          }
                          helperText="Optional; leave blank for generic assumptions."
                          InputProps={{
                            endAdornment: infoIcon(
                              "Optional name of the contracted offtaker; leave blank for generic assumptions."
                            ),
                          }}
                        />

                        <FormControl fullWidth>
                          <InputLabel>Credit Support</InputLabel>
                          <Select
                            label="Credit Support"
                            value={tollingFixedCapacity.bankability?.creditSupportType}
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "bankability.creditSupportType",
                                e.target.value
                              )
                            }
                          >
                            {CREDIT_SUPPORT_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Type of security backing the fee, e.g. parent or bank guarantee, cash, or letter of credit.
                          </FormHelperText>
                        </FormControl>

                        {tollingFixedCapacity.bankability?.creditSupportType !== "none" && (
                          <TextField
                            fullWidth
                            label="Credit Support Months"
                            type="number"
                            value={tollingFixedCapacity.bankability?.creditSupportMonths ?? ""}
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "bankability.creditSupportMonths",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(fixedCapacityValidation.creditSupportMonths)}
                            helperText={
                              fixedCapacityValidation.creditSupportMonths ||
                              "Months of contracted fixed-fee exposure covered."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Number of months of contracted-fee exposure the credit support covers."
                              ),
                            }}
                          />
                        )}

                        <TextField
                          fullWidth
                          label="Guaranteed Availability (%)"
                          type="number"
                          value={
                            tollingFixedCapacity.performanceRegime?.guaranteedAvailabilityPct ?? ""
                          }
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.guaranteedAvailabilityPct",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.guaranteedAvailability)}
                          helperText={
                            fixedCapacityValidation.guaranteedAvailability ||
                            "Optional operational guarantee."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Contractual availability the project guarantees; shortfalls can trigger penalties."
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Penalty Cap (% of annual fee)"
                          type="number"
                          value={
                            tollingFixedCapacity.performanceRegime?.penaltyCapPctOfAnnualFee ?? ""
                          }
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.penaltyCapPctOfAnnualFee",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.penaltyCap)}
                          helperText={
                            fixedCapacityValidation.penaltyCap ||
                            "Optional annual cap on fixed-fee penalties."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Maximum annual penalty expressed as a percentage of the annual fee."
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Planned Outage Allowance (hours)"
                          type="number"
                          value={
                            tollingFixedCapacity.performanceRegime
                              ?.plannedOutageAllowanceHours ?? ""
                          }
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.plannedOutageAllowanceHours",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.plannedOutage)}
                          helperText={
                            fixedCapacityValidation.plannedOutage ||
                            "Optional annual outage allowance before penalties."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Annual maintenance-outage hours allowed before availability penalties apply."
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Guaranteed RTE (%)"
                          type="number"
                          value={tollingFixedCapacity.performanceRegime?.guaranteedRtePct ?? ""}
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.guaranteedRtePct",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.guaranteedRte)}
                          helperText={
                            fixedCapacityValidation.guaranteedRte ||
                            "Optional round-trip efficiency guarantee."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Round-trip efficiency the project guarantees to the offtaker."
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Max Annual Cycles"
                          type="number"
                          value={tollingFixedCapacity.performanceRegime?.maxAnnualCycles ?? ""}
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.maxAnnualCycles",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.maxAnnualCycles)}
                          helperText={
                            fixedCapacityValidation.maxAnnualCycles ||
                            "Optional cycling limit. Blank means not specified."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Contractual limit on full battery cycles per year; blank means unspecified."
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Max Annual Throughput (MWh)"
                          type="number"
                          value={
                            tollingFixedCapacity.performanceRegime?.maxAnnualThroughputMWh ?? ""
                          }
                          onChange={(e) =>
                            handleTollingFixedCapacityNestedField(
                              "performanceRegime.maxAnnualThroughputMWh",
                              e.target.value === "" ? null : Number(e.target.value)
                            )
                          }
                          error={Boolean(fixedCapacityValidation.maxAnnualThroughput)}
                          helperText={
                            fixedCapacityValidation.maxAnnualThroughput ||
                            "Optional throughput limit. Blank means not specified."
                          }
                          InputProps={{
                            endAdornment: infoIcon(
                              "Contractual limit on annual energy throughput in MWh; blank means unspecified."
                            ),
                          }}
                        />

                        <FormControl fullWidth>
                          <InputLabel>Degradation Responsibility</InputLabel>
                          <Select
                            label="Degradation Responsibility"
                            value={
                              tollingFixedCapacity.performanceRegime
                                ?.degradationResponsibility
                            }
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "performanceRegime.degradationResponsibility",
                                e.target.value
                              )
                            }
                          >
                            {RESPONSIBILITY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Which party bears battery capacity degradation over the contract.
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>Augmentation Responsibility</InputLabel>
                          <Select
                            label="Augmentation Responsibility"
                            value={
                              tollingFixedCapacity.performanceRegime
                                ?.augmentationResponsibility
                            }
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "performanceRegime.augmentationResponsibility",
                                e.target.value
                              )
                            }
                          >
                            {RESPONSIBILITY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Which party funds capacity augmentation needed to maintain performance.
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>Charging Cost Responsibility</InputLabel>
                          <Select
                            label="Charging Cost Responsibility"
                            value={
                              tollingFixedCapacity.performanceRegime
                                ?.chargingCostResponsibility
                            }
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "performanceRegime.chargingCostResponsibility",
                                e.target.value
                              )
                            }
                          >
                            {RESPONSIBILITY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Which party pays the cost of charging energy.
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>Dispatch Rights</InputLabel>
                          <Select
                            label="Dispatch Rights"
                            value={tollingFixedCapacity.performanceRegime?.dispatchRights}
                            onChange={(e) =>
                              handleTollingFixedCapacityNestedField(
                                "performanceRegime.dispatchRights",
                                e.target.value
                              )
                            }
                          >
                            {RESPONSIBILITY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Which party controls how the battery is dispatched.
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </>
            )}

            {/* ---------------- Tolling: Fixed Capacity legacy layout (disabled) ---------------- */}
            {false && cfg.settlementType === "TollingFixed" && (
              <>
                {/* Row 1 */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Tolling Fee (€/MW-year)"
                      type="number"
                      value={cfg.tollingFixedFeePerMWYear}
                      onChange={(e) =>
                        handleField("tollingFixedFeePerMWYear", Number(e.target.value))
                      }
                      helperText="Fixed annual payment per MW of contracted battery capacity."
                    />

                    <TextField
                      fullWidth
                      label="Contract Term (years)"
                      type="number"
                      value={cfg.tollingContractYears}
                      onChange={(e) =>
                        handleField("tollingContractYears", Number(e.target.value))
                      }
                      helperText="Typical bankable duration for the tolling agreement."
                    />

                    <TextField
                      fullWidth
                      label="Availability Factor (%)"
                      type="number"
                      value={cfg.tollingAvailabilityPct}
                      onChange={(e) => {
                        const v = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                        handleField("tollingAvailabilityPct", v);
                      }}
                      helperText="Expected technical availability used for fee adjustment."
                    />

                    <TextField
                      fullWidth
                      label="Penalty Rate (€/year)"
                      type="number"
                      value={cfg.tollingPenaltyAnnual}
                      onChange={(e) =>
                        handleField("tollingPenaltyAnnual", Number(e.target.value))
                      }
                      helperText="Optional annual penalty deduction for downtime / underperformance."
                    />
                  </Box>
                </Grid>

                {/* Row 2 */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        label="Settlement Frequency"
                        value={cfg.tollingSettlementFrequency}
                        onChange={(e) => handleField("tollingSettlementFrequency", e.target.value)}
                      >
                        <MenuItem value="Monthly">Monthly</MenuItem>
                        <MenuItem value="Quarterly">Quarterly</MenuItem>
                        <MenuItem value="Annually">Annually</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Annual Escalation (%)"
                      type="number"
                      value={cfg.tollingEscalationPct}
                      onChange={(e) =>
                        handleField(
                          "tollingEscalationPct",
                          Math.min(100, Math.max(0, Number(e.target.value) || 0))
                        )
                      }
                      helperText="Optional annual increase in contracted payment."
                    />

                    <FormControl fullWidth>
                      <InputLabel>Capacity Basis</InputLabel>
                      <Select
                        label="Capacity Basis"
                        value={cfg.tollingCapacityBasis}
                        onChange={(e) => handleField("tollingCapacityBasis", e.target.value)}
                      >
                        <MenuItem value="InstalledMW">Installed Power (MW)</MenuItem>
                        <MenuItem value="ContractedMW">Contracted Power (MW)</MenuItem>
                        <MenuItem value="AvailableMW">Available Power (MW)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Best use case
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited for fully contracted, bankable BESS cases.
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {/* ---------------- Tolling: Floor + Upside ---------------- */}
            {cfg.settlementType === "TollingFloorUpside" && (
              <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Floor Input Mode</InputLabel>
                      <Select
                        label="Floor Input Mode"
                        value={tollingFloorUpside.floorInputMode}
                        onChange={(e) =>
                          handleTollingFloorUpsideField("floorInputMode", e.target.value)
                        }
                      >
                        {FLOOR_INPUT_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select whether the floor is entered directly or quoted by contracted capacity.
                      </FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label={getFloorAmountLabel(tollingFloorUpside.floorInputMode)}
                      type="number"
                      value={tollingFloorUpside.floorAmount}
                      onChange={(e) => {
                        const val = e.target.value === "" ? "" : Number(e.target.value);
                        handleTollingFloorUpsideField("floorAmount", val, "tollingRevenueFloor");
                      }}
                      error={Boolean(tollingValidation.floorAmount)}
                      helperText={
                        tollingValidation.floorAmount ||
                        getFloorAmountHelper(tollingFloorUpside.floorInputMode)
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Guaranteed minimum revenue floor in the selected unit; the project earns at least this."
                        ),
                      }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        label="Settlement Frequency"
                        value={tollingFloorUpside.settlementFrequency}
                        onChange={(e) =>
                          handleTollingFloorUpsideField(
                            "settlementFrequency",
                            e.target.value,
                            "tollingSettlementFrequency"
                          )
                        }
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                        <MenuItem value="annual">Annual</MenuItem>
                      </Select>
                      <FormHelperText>Period used for invoicing and preview values.</FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Project Upside Share (%)"
                      type="number"
                      value={tollingFloorUpside.projectUpsideSharePct}
                      onChange={(e) => {
                        const v = e.target.value === "" ? "" : Number(e.target.value);
                        handleTollingFloorUpsideField(
                          "projectUpsideSharePct",
                          v,
                          "tollingUpsideSharePct"
                        );
                      }}
                      error={Boolean(tollingValidation.projectUpsideShare)}
                      helperText={
                        tollingValidation.projectUpsideShare ||
                        "Percentage of merchant upside kept by the project above the floor."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Share of merchant upside above the floor that the project keeps; the rest goes to the offtaker."
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Contract Term (years)"
                      type="number"
                      value={tollingFloorUpside.contractTermYears}
                      onChange={(e) =>
                        handleTollingFloorUpsideField(
                          "contractTermYears",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingContractYears"
                        )
                      }
                      error={Boolean(tollingValidation.contractTerm)}
                      helperText={
                        tollingValidation.contractTerm || "Duration of the floor + upside arrangement."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Number of years the floor and upside-share arrangement applies."
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Floor Escalation (%/year)"
                      type="number"
                      value={tollingFloorUpside.floorEscalationPct}
                      onChange={(e) =>
                        handleTollingFloorUpsideField(
                          "floorEscalationPct",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingEscalationPct"
                        )
                      }
                      color={tollingValidation.escalationWarning ? "warning" : "primary"}
                      helperText={
                        tollingValidation.escalationWarning ||
                        "Annual escalation applied to the contracted floor, not to merchant revenue unless separately configured."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Annual percentage increase applied to the contracted revenue floor."
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Floor Availability Adjustment (%)"
                      type="number"
                      value={tollingFloorUpside.availabilityAdjustmentPct}
                      onChange={(e) => {
                        const v = e.target.value === "" ? "" : Number(e.target.value);
                        handleTollingFloorUpsideField(
                          "availabilityAdjustmentPct",
                          v,
                          "tollingAvailabilityPct"
                        );
                      }}
                      error={Boolean(tollingValidation.availability)}
                      helperText={
                        tollingValidation.availability ||
                        "Used to derive the effective floor if availability adjustment is enabled."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Percentage the floor is reduced for expected unavailability, when the adjustment switch is on."
                        ),
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 56,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(tollingFloorUpside.applyAvailabilityToFloor)}
                            onChange={(e) =>
                              handleTollingFloorUpsideField(
                                "applyAvailabilityToFloor",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <MaybeTooltip
                            title="When on, the floor is scaled by the Floor Availability Adjustment percentage."
                            placement="top"
                            arrow
                          >
                            <span>Apply availability adjustment to floor</span>
                          </MaybeTooltip>
                        }
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
                      <InputLabel>Merchant Optimization</InputLabel>
                      <Select
                        label="Merchant Optimization"
                        value={tollingFloorUpside.merchantOptimization ? "Yes" : "No"}
                        onChange={(e) =>
                          handleTollingFloorUpsideField(
                            "merchantOptimization",
                            e.target.value === "Yes",
                            "useMerchantOptimization"
                          )
                        }
                      >
                        <MenuItem value="Yes">Enabled</MenuItem>
                        <MenuItem value="No">Disabled</MenuItem>
                      </Select>
                      <FormHelperText>
                        If enabled, merchant dispatch value is used for tolling settlement and trader/offtaker economics.
                      </FormHelperText>
                    </FormControl>

                    {isCapacityBasedFloorMode ? (
                      <TextField
                        fullWidth
                        sx={{ gridColumn: { xs: "1", md: "span 2" } }}
                        label="Contracted Capacity (MW)"
                        type="number"
                        value={tollingFloorUpside.contractedCapacityMw ?? ""}
                        onChange={(e) =>
                          handleTollingFloorUpsideField(
                            "contractedCapacityMw",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(tollingValidation.contractedCapacity)}
                        helperText={
                          tollingValidation.contractedCapacity ||
                          "Capacity used to calculate the floor when the floor is quoted on a MW or kW basis."
                        }
                        InputProps={{
                          endAdornment: infoIcon(
                            "Battery MW used to calculate the floor when the floor is quoted per MW or per kW."
                          ),
                        }}
                      />
                    ) : (
                      <Alert severity="info" sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
                        Capacity is not used to calculate this explicit floor.
                      </Alert>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {renderMarketRevenueStack(
                    tollingFloorUpside.includedRevenueStreams,
                    (key, checked, nextStreams) =>
                      handleTollingFloorUpsideNestedField(
                        nextStreams ? "includedRevenueStreams" : `includedRevenueStreams.${key}`,
                        nextStreams || checked
                      ),
                    "floor_upside",
                    isProjectGR,
                    tollingFloorUpside.balancingV1,
                    (nextConfig) => handleTollingFloorUpsideNestedField("balancingV1", nextConfig),
                    installedBessPowerMw,
                    cfg.biddingZone,
                    tollingFloorUpside.capacityPayments,
                    (nextConfig) => handleTollingFloorUpsideNestedField("capacityPayments", nextConfig),
                    tollingFloorUpside.guaranteesOfOrigin,
                    (nextConfig) => handleTollingFloorUpsideNestedField("guaranteesOfOrigin", nextConfig),
                    regulatoryFramework.mode,
                    stationContext,
                    isGreek
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Effective Floor Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      This is the floor used for settlement after availability adjustment. It is calculated automatically and cannot be edited directly. The backend recalculates this value from the raw inputs.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Raw floor"
                        value={`${formatCurrency(tollingFloorPreview.rawFloor)}/${tollingFloorPreview.rawFloorUnit}`}
                        InputProps={{ readOnly: true }}
                        helperText={
                          isCapacityBasedFloorMode
                            ? "Quoted raw floor before settlement-frequency conversion."
                            : "Explicit floor amount before availability adjustment."
                        }
                      />
                      <TextField
                        fullWidth
                        label="Availability adjustment"
                        value={
                          tollingFloorUpside.applyAvailabilityToFloor
                            ? formatPercent(tollingFloorPreview.availabilityPct)
                            : "Not applied"
                        }
                        InputProps={{ readOnly: true }}
                        helperText="Availability factor applied to the raw settlement floor."
                      />
                      <TextField
                        fullWidth
                        label="Effective Floor — automatically calculated"
                        value={`${formatCurrency(
                          tollingFloorPreview.effectiveFloorPerSettlement
                        )}/${getSettlementPeriodLabel(tollingFloorPreview.settlementFrequency)}`}
                        InputProps={{ readOnly: true }}
                        helperText="Read-only settlement floor after availability adjustment."
                      />
                      <TextField
                        fullWidth
                        label="Annualized effective floor"
                        value={`${formatCurrency(tollingFloorPreview.annualizedEffectiveFloor)}/year`}
                        InputProps={{ readOnly: true }}
                        helperText="Annual equivalent of the effective settlement-period floor."
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Bankability & Contract Assumptions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        These fields support bankability disclosure and contract review. They are not applied as financial adjustments unless explicitly modelled by the backend.
                      </Alert>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                          gap: 3,
                          mb: 3,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Counterparty Status</InputLabel>
                          <Select
                            label="Counterparty Status"
                            value={
                              tollingFloorUpside.bankability?.counterpartyStatus ??
                              "generic_assumption"
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "bankability.counterpartyStatus",
                                e.target.value
                              )
                            }
                          >
                            {COUNTERPARTY_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Indicates whether the floor is only assumed or backed by a counterparty document.
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>Counterparty Type</InputLabel>
                          <Select
                            label="Counterparty Type"
                            value={
                              tollingFloorUpside.bankability?.counterpartyType ??
                              "not_specified"
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "bankability.counterpartyType",
                                e.target.value
                              )
                            }
                          >
                            {COUNTERPARTY_TYPE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Category of the offtaker (utility, trader, corporate, etc.) used for bankability context.
                          </FormHelperText>
                        </FormControl>

                        <TextField
                          fullWidth
                          label="Counterparty Name"
                          value={tollingFloorUpside.bankability?.counterpartyName ?? ""}
                          onChange={(e) =>
                            handleTollingFloorUpsideNestedField(
                              "bankability.counterpartyName",
                              e.target.value
                            )
                          }
                          helperText="Optional. Leave blank if no specific counterparty is assumed."
                          InputProps={{
                            endAdornment: infoIcon(
                              "Optional name of the contracted offtaker; leave blank if none is assumed."
                            ),
                          }}
                        />

                        <FormControl fullWidth>
                          <InputLabel>Credit Support Type</InputLabel>
                          <Select
                            label="Credit Support Type"
                            value={
                              tollingFloorUpside.bankability?.creditSupportType ??
                              "none"
                            }
                            onChange={(e) => {
                              const creditSupportType = e.target.value;
                              handleTollingFloorUpsideField("bankability", {
                                ...(tollingFloorUpside.bankability || {}),
                                creditSupportType,
                                creditSupportMonths:
                                  creditSupportType === "none"
                                    ? null
                                    : (tollingFloorUpside.bankability?.creditSupportMonths ?? null),
                              });
                            }}
                          >
                            {CREDIT_SUPPORT_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Type of security backing the floor, e.g. parent or bank guarantee, cash, or letter of credit.
                          </FormHelperText>
                        </FormControl>

                        {(tollingFloorUpside.bankability?.creditSupportType ?? "none") !== "none" && (
                          <TextField
                            fullWidth
                            label="Credit Support Months"
                            type="number"
                            value={tollingFloorUpside.bankability?.creditSupportMonths ?? ""}
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "bankability.creditSupportMonths",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.creditSupportMonths)}
                            helperText={
                              tollingValidation.creditSupportMonths ||
                              "Number of months of floor payment covered by credit support."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Number of months of floor payments the credit support covers."
                              ),
                            }}
                          />
                        )}

                        <FormControl fullWidth>
                          <InputLabel>Merchant Value Basis</InputLabel>
                          <Select
                            label="Merchant Value Basis"
                            value={
                              tollingFloorUpside.merchantValueBasis ??
                              "net_after_charging_cost"
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideField(
                                "merchantValueBasis",
                                e.target.value
                              )
                            }
                          >
                            {MERCHANT_VALUE_BASIS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Defines what merchant value is compared against the floor and used for upside sharing. Current backend settlement should continue using the supported merchant-value basis unless otherwise implemented.
                          </FormHelperText>
                        </FormControl>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          Performance & Penalty Regime
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                            gap: 3,
                          }}
                        >
                          <TextField
                            fullWidth
                            label="Guaranteed Availability (%)"
                            type="number"
                            value={
                              tollingFloorUpside.performanceRegime?.guaranteedAvailabilityPct ??
                              ""
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.guaranteedAvailabilityPct",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.guaranteedAvailability)}
                            helperText={
                              tollingValidation.guaranteedAvailability ||
                              "Optional contractual availability guarantee for disclosure. This does not create an additional revenue adjustment unless explicitly modelled by the backend. Defaulted from the floor availability adjustment. You may clear it if no contractual availability guarantee is specified."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Contractual availability the project guarantees; for disclosure unless modelled by the backend."
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Penalty Cap (% of annual floor)"
                            type="number"
                            value={
                              tollingFloorUpside.performanceRegime?.penaltyCapPctOfAnnualFloor ??
                              ""
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.penaltyCapPctOfAnnualFloor",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.penaltyCap)}
                            helperText={tollingValidation.penaltyCap || "Optional cap on annual floor penalties."}
                            InputProps={{
                              endAdornment: infoIcon(
                                "Maximum annual penalty expressed as a percentage of the annual floor."
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Planned Outage Allowance (hours/year)"
                            type="number"
                            value={
                              tollingFloorUpside.performanceRegime?.plannedOutageAllowanceHours ??
                              ""
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.plannedOutageAllowanceHours",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.plannedOutage)}
                            helperText={tollingValidation.plannedOutage || "Optional planned outage allowance."}
                            InputProps={{
                              endAdornment: infoIcon(
                                "Annual maintenance-outage hours allowed before availability penalties apply."
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Guaranteed RTE (%)"
                            type="number"
                            value={tollingFloorUpside.performanceRegime?.guaranteedRtePct ?? ""}
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.guaranteedRtePct",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.guaranteedRte)}
                            helperText={
                              tollingValidation.guaranteedRte ||
                              "Optional contractual round-trip efficiency guarantee. Leave blank if not specified in the tolling/offtake terms."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Round-trip efficiency the project guarantees to the offtaker."
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Max Annual Cycles"
                            type="number"
                            value={tollingFloorUpside.performanceRegime?.maxAnnualCycles ?? ""}
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.maxAnnualCycles",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.maxAnnualCycles)}
                            helperText={
                              tollingValidation.maxAnnualCycles ||
                              "Optional contractual cycle limit. Leave blank if no cycle cap is specified."
                            }
                            InputProps={{
                              endAdornment: infoIcon(
                                "Contractual limit on full battery cycles per year; blank means unspecified."
                              ),
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Max Annual Throughput (MWh/year)"
                            type="number"
                            value={
                              tollingFloorUpside.performanceRegime?.maxAnnualThroughputMWh ??
                              ""
                            }
                            onChange={(e) =>
                              handleTollingFloorUpsideNestedField(
                                "performanceRegime.maxAnnualThroughputMWh",
                                e.target.value === "" ? null : Number(e.target.value)
                              )
                            }
                            error={Boolean(tollingValidation.maxAnnualThroughput)}
                            helperText={tollingValidation.maxAnnualThroughput || "Optional annual throughput limit."}
                            InputProps={{
                              endAdornment: infoIcon(
                                "Contractual limit on annual energy throughput in MWh; blank means unspecified."
                              ),
                            }}
                          />

                          <FormControl fullWidth>
                            <InputLabel>Degradation Responsibility</InputLabel>
                            <Select
                              label="Degradation Responsibility"
                              value={
                                tollingFloorUpside.performanceRegime?.degradationResponsibility ??
                                "not_specified"
                              }
                              onChange={(e) =>
                                handleTollingFloorUpsideNestedField(
                                  "performanceRegime.degradationResponsibility",
                                  e.target.value
                                )
                              }
                            >
                              {RESPONSIBILITY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Which party bears battery capacity degradation over the contract.
                            </FormHelperText>
                          </FormControl>

                          <FormControl fullWidth>
                            <InputLabel>Augmentation Responsibility</InputLabel>
                            <Select
                              label="Augmentation Responsibility"
                              value={
                                tollingFloorUpside.performanceRegime?.augmentationResponsibility ??
                                "not_specified"
                              }
                              onChange={(e) =>
                                handleTollingFloorUpsideNestedField(
                                  "performanceRegime.augmentationResponsibility",
                                  e.target.value
                                )
                              }
                            >
                              {RESPONSIBILITY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Which party funds capacity augmentation needed to maintain performance.
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Best use case
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited where downside protection is required while preserving partial merchant upside.
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {/* ---------------- Hybrid: Partial Tolling ---------------- */}
            {cfg.settlementType === "HybridPartial" && (
              <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Allocation Basis</InputLabel>
                      <Select
                        label="Allocation Basis"
                        value={hybridPartialTolling.allocationBasis}
                        onChange={(e) => handleHybridPartialField("allocationBasis", e.target.value)}
                      >
                        {HYBRID_PARTIAL_ALLOCATION_BASIS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Defines how the contracted and merchant portions are allocated. For V1, capacity share and revenue share both allocate merchant value proportionally unless backend implements physical partitioning.
                      </FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Contracted Share (%)"
                      type="number"
                      value={hybridPartialTolling.contractedSharePct}
                      onChange={(e) => {
                        const contracted = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                        handleHybridPartialField("contractedSharePct", contracted);
                      }}
                      error={Boolean(hybridPartialValidation.contractedShare)}
                      helperText={
                        hybridPartialValidation.contractedShare ||
                        "Share of project capacity/economics covered by the fixed tolling contract."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Percentage of the project covered by the fixed tolling contract; the remainder stays merchant."
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Merchant Share (%)"
                      type="number"
                      value={Math.max(0, 100 - Number(hybridPartialTolling.contractedSharePct || 0))}
                      InputProps={{ readOnly: true }}
                      helperText="Share retained as merchant exposure by the project."
                    />

                    <FormControl fullWidth>
                      <InputLabel>Fee Input Mode</InputLabel>
                      <Select
                        label="Fee Input Mode"
                        value={hybridPartialTolling.feeInputMode}
                        onChange={(e) => handleHybridPartialField("feeInputMode", e.target.value)}
                      >
                        {HYBRID_PARTIAL_FEE_INPUT_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Capacity-based modes apply to the contracted capacity only. Absolute modes are interpreted as the fixed payment for the contracted portion.
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      label={getHybridPartialFeeAmountLabel(hybridPartialTolling.feeInputMode)}
                      type="number"
                      value={hybridPartialTolling.feeAmount ?? ""}
                      onChange={(e) =>
                        handleHybridPartialField(
                          "feeAmount",
                          e.target.value === "" ? null : Number(e.target.value),
                          "hybridTollFeePerMWYear"
                        )
                      }
                      error={Boolean(hybridPartialValidation.feeAmount)}
                      helperText={
                        hybridPartialValidation.feeAmount ||
                        "Fixed payment applied to the contracted share before availability adjustment, penalties, and escalation."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Fixed payment on the contracted share in the selected unit, before availability adjustment, penalties and escalation."
                        ),
                      }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        label="Settlement Frequency"
                        value={hybridPartialTolling.settlementFrequency}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "settlementFrequency",
                            e.target.value,
                            "tollingSettlementFrequency"
                          )
                        }
                      >
                        {HYBRID_PARTIAL_SETTLEMENT_FREQUENCIES.map((frequency) => (
                          <MenuItem key={frequency.value} value={frequency.value}>
                            {frequency.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Period used for invoicing and preview values.</FormHelperText>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Contract Term (years)"
                      type="number"
                      value={hybridPartialTolling.contractTermYears}
                      onChange={(e) =>
                        handleHybridPartialField(
                          "contractTermYears",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingContractYears"
                        )
                      }
                      error={Boolean(hybridPartialValidation.contractTerm)}
                      helperText={
                        hybridPartialValidation.contractTerm ||
                        "Duration of the partial tolling arrangement."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Number of years the partial tolling arrangement applies."
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Annual Escalation (%/year)"
                      type="number"
                      value={hybridPartialTolling.annualEscalationPct}
                      onChange={(e) =>
                        handleHybridPartialField(
                          "annualEscalationPct",
                          e.target.value === "" ? "" : Number(e.target.value),
                          "tollingEscalationPct"
                        )
                      }
                      helperText="Annual increase applied to the contracted fixed tolling fee."
                      InputProps={{
                        endAdornment: infoIcon(
                          "Annual percentage increase applied to the contracted fixed tolling fee."
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Capacity Basis</InputLabel>
                      <Select
                        label="Capacity Basis"
                        value={hybridPartialTolling.capacityBasis}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "capacityBasis",
                            e.target.value,
                            "tollingCapacityBasis"
                          )
                        }
                      >
                        <MenuItem value="installed_power_mw">Installed Power (MW)</MenuItem>
                        <MenuItem value="contracted_capacity_mw">Contracted Capacity (MW)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Installed Power uses installed BESS MW multiplied by Contracted Share.
                      </FormHelperText>
                    </FormControl>

                    {hybridPartialTolling.capacityBasis === "contracted_capacity_mw" ? (
                      <TextField
                        fullWidth
                        label="Contracted Capacity (MW)"
                        type="number"
                        value={hybridPartialTolling.contractedCapacityMw ?? ""}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "contractedCapacityMw",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(hybridPartialValidation.contractedCapacity)}
                        helperText={
                          hybridPartialValidation.contractedCapacity ||
                          hybridPartialValidation.contractedCapacityWarning ||
                          "Explicit contracted capacity used for the fixed tolling payment."
                        }
                        InputProps={{
                          endAdornment: infoIcon(
                            "Explicit battery MW the contracted fee is calculated on when Capacity Basis is contracted capacity."
                          ),
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Installed BESS Power (MW)"
                        value={installedBessPowerMw == null ? "Not available" : installedBessPowerMw}
                        InputProps={{
                          readOnly: true,
                          endAdornment: infoIcon(
                            "Total installed BESS discharge power derived from the selected battery equipment; used as the capacity basis."
                          ),
                        }}
                        helperText="Derived from selected BESS equipment."
                      />
                    )}

                    <TextField
                      fullWidth
                      label="Availability Adjustment (%)"
                      type="number"
                      value={hybridPartialTolling.availabilityAdjustmentPct ?? ""}
                      onChange={(e) =>
                        handleHybridPartialField(
                          "availabilityAdjustmentPct",
                          e.target.value === "" ? null : Number(e.target.value),
                          "tollingAvailabilityPct"
                        )
                      }
                      error={Boolean(hybridPartialValidation.availability)}
                      helperText={
                        hybridPartialValidation.availability ||
                        "Paid availability factor applied to the contracted fixed fee. This is applied once and is not a second penalty."
                      }
                      InputProps={{
                        endAdornment: infoIcon(
                          "Paid availability factor applied to the contracted fixed fee, when the adjustment switch is on."
                        ),
                      }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 56 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(hybridPartialTolling.applyAvailabilityAdjustment)}
                            onChange={(e) =>
                              handleHybridPartialField(
                                "applyAvailabilityAdjustment",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <MaybeTooltip
                            title="When on, the contracted fee is scaled by the Availability Adjustment percentage."
                            placement="top"
                            arrow
                          >
                            <span>Apply availability adjustment to contracted fee</span>
                          </MaybeTooltip>
                        }
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(12, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                      <InputLabel>Penalty Mode</InputLabel>
                      <Select
                        label="Penalty Mode"
                        value={hybridPartialTolling.penaltyMode}
                        onChange={(e) => handleHybridPartialField("penaltyMode", e.target.value)}
                      >
                        {HYBRID_PARTIAL_PENALTY_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Optional deduction from the contracted fixed fee.</FormHelperText>
                    </FormControl>

                    {hybridPartialTolling.penaltyMode === "fixed_annual_eur" && (
                      <TextField
                        fullWidth
                        sx={{ gridColumn: { xs: "1", md: "span 3" } }}
                        label="Annual Penalty (EUR/year)"
                        type="number"
                        value={hybridPartialTolling.annualPenaltyEur ?? ""}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "annualPenaltyEur",
                            e.target.value === "" ? null : Number(e.target.value),
                            "tollingPenaltyAnnual"
                          )
                        }
                        error={Boolean(hybridPartialValidation.annualPenalty)}
                        helperText={hybridPartialValidation.annualPenalty || "Annual deduction from the contracted fixed fee."}
                        InputProps={{
                          endAdornment: infoIcon(
                            "Fixed euro amount deducted from the contracted fixed fee each year."
                          ),
                        }}
                      />
                    )}

                    {hybridPartialTolling.penaltyMode === "percent_of_gross_fee" && (
                      <TextField
                        fullWidth
                        sx={{ gridColumn: { xs: "1", md: "span 3" } }}
                        label="Penalty (% of gross fee)"
                        type="number"
                        value={hybridPartialTolling.penaltyPctOfGrossFee ?? ""}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "penaltyPctOfGrossFee",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(hybridPartialValidation.penaltyPct)}
                        helperText={hybridPartialValidation.penaltyPct || "Annual penalty calculated as a share of gross contracted fee."}
                        InputProps={{
                          endAdornment: infoIcon(
                            "Annual penalty expressed as a percentage of the gross contracted fee."
                          ),
                        }}
                      />
                    )}

                    {hybridPartialTolling.penaltyMode === "fixed_per_settlement_period" && (
                      <TextField
                        fullWidth
                        sx={{ gridColumn: { xs: "1", md: "span 3" } }}
                        label="Penalty (EUR/settlement period)"
                        type="number"
                        value={hybridPartialTolling.penaltyPerSettlementPeriodEur ?? ""}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "penaltyPerSettlementPeriodEur",
                            e.target.value === "" ? null : Number(e.target.value)
                          )
                        }
                        error={Boolean(hybridPartialValidation.periodPenalty)}
                        helperText={hybridPartialValidation.periodPenalty || "Deduction applied once per settlement period."}
                        InputProps={{
                          endAdornment: infoIcon(
                            "Fixed euro penalty deducted once per settlement period."
                          ),
                        }}
                      />
                    )}

                    {hybridPartialTolling.penaltyMode === "none" && (
                      <Alert severity="info" sx={{ alignItems: "center", gridColumn: { xs: "1", md: "span 3" } }}>
                        No contracted-fee penalty is applied.
                      </Alert>
                    )}

                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                      <InputLabel>Merchant Optimization</InputLabel>
                      <Select
                        label="Merchant Optimization"
                        value={hybridPartialTolling.merchantOptimization ? "Yes" : "No"}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "merchantOptimization",
                            e.target.value === "Yes",
                            "useMerchantOptimization"
                          )
                        }
                      >
                        <MenuItem value="Yes">Enabled</MenuItem>
                        <MenuItem value="No">Disabled</MenuItem>
                      </Select>
                      <FormHelperText>
                        If enabled, merchant dispatch value is calculated and allocated between contracted trader/offtaker economics and project merchant exposure.
                      </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                      <InputLabel>Merchant Value Basis</InputLabel>
                      <Select
                        label="Merchant Value Basis"
                        value={hybridPartialTolling.merchantValueBasis}
                        onChange={(e) => handleHybridPartialField("merchantValueBasis", e.target.value)}
                      >
                        {MERCHANT_VALUE_BASIS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        How merchant value is interpreted when allocating between the project and trader/offtaker.
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(12, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                      <InputLabel>Contracted Share Merchant Recipient</InputLabel>
                      <Select
                        label="Contracted Share Merchant Recipient"
                        value={hybridPartialTolling.contractedShareMerchantRecipient}
                        onChange={(e) =>
                          handleHybridPartialField(
                            "contractedShareMerchantRecipient",
                            e.target.value
                          )
                        }
                      >
                        {HYBRID_PARTIAL_RECIPIENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Defines who receives merchant value associated with the contracted share. Typical partial tolling assigns this value to the trader/offtaker.
                      </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                      <InputLabel>Merchant Share Revenue Recipient</InputLabel>
                      <Select
                        label="Merchant Share Revenue Recipient"
                        value={hybridPartialTolling.merchantShareRevenueRecipient}
                        onChange={(e) =>
                          handleHybridPartialField("merchantShareRevenueRecipient", e.target.value)
                        }
                      >
                        {HYBRID_PARTIAL_RECIPIENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Defines who receives merchant value associated with the uncontracted share. Typical partial tolling keeps this with the project owner.
                      </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth sx={{ gridColumn: { xs: "1", md: "span 6" } }}>
                      <InputLabel>Post-contract Revenue Mode</InputLabel>
                      <Select
                        label="Post-contract Revenue Mode"
                        value={hybridPartialTolling.postContractRevenueMode}
                        onChange={(e) =>
                          handleHybridPartialField("postContractRevenueMode", e.target.value)
                        }
                      >
                        {HYBRID_PARTIAL_POST_CONTRACT_REVENUE_MODE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Revenue assumption after the contract term ends - full merchant reversion, zero, or continue the hybrid structure.
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {renderMarketRevenueStack(
                    hybridPartialTolling.includedRevenueStreams,
                    (key, checked, nextStreams) =>
                      handleHybridPartialNestedField(
                        nextStreams ? "includedRevenueStreams" : `includedRevenueStreams.${key}`,
                        nextStreams || checked
                      ),
                    "hybrid_partial",
                    isProjectGR,
                    hybridPartialTolling.balancingV1,
                    (nextConfig) => handleHybridPartialNestedField("balancingV1", nextConfig),
                    installedBessPowerMw,
                    cfg.biddingZone,
                    hybridPartialTolling.capacityPayments,
                    (nextConfig) => handleHybridPartialNestedField("capacityPayments", nextConfig),
                    hybridPartialTolling.guaranteesOfOrigin,
                    (nextConfig) => handleHybridPartialNestedField("guaranteesOfOrigin", nextConfig),
                    regulatoryFramework.mode,
                    stationContext,
                    isGreek
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Hybrid Partial Tolling Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      Project revenue = net contracted fee on contracted share + merchant revenue on merchant share.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Trader/offtaker economics = merchant value from contracted share - contracted fixed fee.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                        gap: 2,
                      }}
                    >
                      <TextField fullWidth label="Installed capacity" value={`${Number(hybridPartialPreview.installedPowerMw || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })} MW`} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Contracted share" value={formatPercent(hybridPartialPreview.contractedSharePct)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Merchant share" value={formatPercent(hybridPartialPreview.merchantSharePct)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Contracted capacity used" value={`${Number(hybridPartialPreview.contractedCapacityMw || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })} MW`} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Merchant-exposed capacity" value={`${Number(hybridPartialPreview.merchantCapacityMw || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })} MW`} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Gross contracted fee/period" value={formatCurrency(hybridPartialPreview.grossContractedFeePerSettlementPeriod)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Net contracted fee/period" value={formatCurrency(hybridPartialPreview.netContractedFeePerSettlementPeriod)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Annualized gross contracted fee" value={formatCurrency(hybridPartialPreview.annualGrossContractedFee)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Annualized net contracted fee" value={formatCurrency(hybridPartialPreview.annualNetContractedFee)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Project merchant-share revenue" value={hybridPartialPreview.projectMerchantRevenue == null ? "Not available" : formatCurrency(hybridPartialPreview.projectMerchantRevenue)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Trader merchant-share value" value={hybridPartialPreview.traderMerchantValue == null ? "Not available" : formatCurrency(hybridPartialPreview.traderMerchantValue)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Estimated Year 1 project revenue" value={hybridPartialPreview.estimatedProjectRevenue == null ? "Not available" : formatCurrency(hybridPartialPreview.estimatedProjectRevenue)} InputProps={{ readOnly: true }} />
                      <TextField fullWidth label="Estimated trader net after contracted fee" value={hybridPartialPreview.estimatedTraderNet == null ? "Not available" : formatCurrency(hybridPartialPreview.estimatedTraderNet)} InputProps={{ readOnly: true }} />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Structure comparison
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}>
                      <b>Tolling - Floor + Upside:</b> Project receives a minimum revenue floor across the full asset plus a share of upside above the floor. Trader/offtaker covers shortfall below the floor and keeps residual upside.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}>
                      <b>Tolling - Fixed Capacity:</b> Project receives a fixed capacity/tolling fee during the contract. Trader/offtaker receives or controls merchant value and pays the fixed fee.
                    </Typography>
                    <Typography variant="body2">
                      <b>Hybrid - Partial Tolling:</b> Project receives a fixed fee on the contracted share and keeps merchant revenue on the merchant share. Trader/offtaker receives merchant value from the contracted share and pays the contracted fee.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Best use case
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited when lenders require contracted revenue on part of the asset, while equity investors want to retain merchant upside on the remaining share.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Bankability & Contract Assumptions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These fields support bankability disclosure and contract review. They are not applied as financial adjustments unless explicitly modelled by the backend.
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                          gap: 3,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Counterparty Status</InputLabel>
                          <Select label="Counterparty Status" value={hybridPartialTolling.bankability?.counterpartyStatus} onChange={(e) => handleHybridPartialNestedField("bankability.counterpartyStatus", e.target.value)}>
                            {COUNTERPARTY_STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>How firm the offtaker is; a signed counterparty is more bankable than a generic assumption.</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Counterparty Type</InputLabel>
                          <Select label="Counterparty Type" value={hybridPartialTolling.bankability?.counterpartyType} onChange={(e) => handleHybridPartialNestedField("bankability.counterpartyType", e.target.value)}>
                            {COUNTERPARTY_TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Category of the offtaker (utility, trader, corporate, etc.) used for bankability context.</FormHelperText>
                        </FormControl>
                        <TextField fullWidth label="Counterparty Name" value={hybridPartialTolling.bankability?.counterpartyName ?? ""} onChange={(e) => handleHybridPartialNestedField("bankability.counterpartyName", e.target.value)} helperText="Optional; leave blank for generic assumptions." InputProps={{ endAdornment: infoIcon("Optional name of the contracted offtaker; leave blank for generic assumptions.") }} />
                        <FormControl fullWidth>
                          <InputLabel>Credit Support Type</InputLabel>
                          <Select label="Credit Support Type" value={hybridPartialTolling.bankability?.creditSupportType} onChange={(e) => handleHybridPartialNestedField("bankability.creditSupportType", e.target.value)}>
                            {CREDIT_SUPPORT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Type of security backing the fee, e.g. parent or bank guarantee, cash, or letter of credit.</FormHelperText>
                        </FormControl>
                        {hybridPartialTolling.bankability?.creditSupportType !== "none" && (
                          <TextField fullWidth label="Credit Support Months" type="number" value={hybridPartialTolling.bankability?.creditSupportMonths ?? ""} onChange={(e) => handleHybridPartialNestedField("bankability.creditSupportMonths", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.creditSupportMonths)} helperText={hybridPartialValidation.creditSupportMonths || "Months of contracted-fee exposure covered."} InputProps={{ endAdornment: infoIcon("Number of months of contracted-fee exposure the credit support covers.") }} />
                        )}
                        <TextField fullWidth label="Guaranteed Availability (%)" type="number" value={hybridPartialTolling.performanceRegime?.guaranteedAvailabilityPct ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.guaranteedAvailabilityPct", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.guaranteedAvailability)} helperText={hybridPartialValidation.guaranteedAvailability || "Optional operational guarantee."} InputProps={{ endAdornment: infoIcon("Contractual availability the project guarantees; shortfalls can trigger penalties.") }} />
                        <TextField fullWidth label="Penalty Cap (% of annual fee)" type="number" value={hybridPartialTolling.performanceRegime?.penaltyCapPctOfAnnualFee ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.penaltyCapPctOfAnnualFee", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.penaltyCap)} helperText={hybridPartialValidation.penaltyCap || "Optional annual cap on contracted-fee penalties."} InputProps={{ endAdornment: infoIcon("Maximum annual penalty expressed as a percentage of the annual fee.") }} />
                        <TextField fullWidth label="Planned Outage Allowance (hours)" type="number" value={hybridPartialTolling.performanceRegime?.plannedOutageAllowanceHours ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.plannedOutageAllowanceHours", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.plannedOutage)} helperText={hybridPartialValidation.plannedOutage || "Optional annual outage allowance before penalties."} InputProps={{ endAdornment: infoIcon("Annual maintenance-outage hours allowed before availability penalties apply.") }} />
                        <TextField fullWidth label="Guaranteed RTE (%)" type="number" value={hybridPartialTolling.performanceRegime?.guaranteedRtePct ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.guaranteedRtePct", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.guaranteedRte)} helperText={hybridPartialValidation.guaranteedRte || "Optional round-trip efficiency guarantee."} InputProps={{ endAdornment: infoIcon("Round-trip efficiency the project guarantees to the offtaker.") }} />
                        <TextField fullWidth label="Max Annual Cycles" type="number" value={hybridPartialTolling.performanceRegime?.maxAnnualCycles ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.maxAnnualCycles", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.maxAnnualCycles)} helperText={hybridPartialValidation.maxAnnualCycles || "Optional cycling limit. Blank means not specified."} InputProps={{ endAdornment: infoIcon("Contractual limit on full battery cycles per year; blank means unspecified.") }} />
                        <TextField fullWidth label="Max Annual Throughput (MWh)" type="number" value={hybridPartialTolling.performanceRegime?.maxAnnualThroughputMWh ?? ""} onChange={(e) => handleHybridPartialNestedField("performanceRegime.maxAnnualThroughputMWh", e.target.value === "" ? null : Number(e.target.value))} error={Boolean(hybridPartialValidation.maxAnnualThroughput)} helperText={hybridPartialValidation.maxAnnualThroughput || "Optional throughput limit. Blank means not specified."} InputProps={{ endAdornment: infoIcon("Contractual limit on annual energy throughput in MWh; blank means unspecified.") }} />
                        <FormControl fullWidth>
                          <InputLabel>Degradation Responsibility</InputLabel>
                          <Select label="Degradation Responsibility" value={hybridPartialTolling.performanceRegime?.degradationResponsibility} onChange={(e) => handleHybridPartialNestedField("performanceRegime.degradationResponsibility", e.target.value)}>
                            {RESPONSIBILITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Which party bears battery capacity degradation over the contract.</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Augmentation Responsibility</InputLabel>
                          <Select label="Augmentation Responsibility" value={hybridPartialTolling.performanceRegime?.augmentationResponsibility} onChange={(e) => handleHybridPartialNestedField("performanceRegime.augmentationResponsibility", e.target.value)}>
                            {RESPONSIBILITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Which party funds capacity augmentation needed to maintain performance.</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Charging Cost Responsibility</InputLabel>
                          <Select label="Charging Cost Responsibility" value={hybridPartialTolling.performanceRegime?.chargingCostResponsibility} onChange={(e) => handleHybridPartialNestedField("performanceRegime.chargingCostResponsibility", e.target.value)}>
                            {RESPONSIBILITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Which party pays the cost of charging energy.</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Dispatch Rights</InputLabel>
                          <Select label="Dispatch Rights" value={hybridPartialTolling.performanceRegime?.dispatchRights} onChange={(e) => handleHybridPartialNestedField("performanceRegime.dispatchRights", e.target.value)}>
                            {RESPONSIBILITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                          </Select>
                          <FormHelperText>Which party controls how the battery is dispatched.</FormHelperText>
                        </FormControl>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </>
            )}

            {/* ---------------- Hybrid: Partial Tolling legacy layout (disabled) ---------------- */}
            {false && cfg.settlementType === "HybridPartial" && (
              <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Contracted Share (%)"
                      type="number"
                      value={cfg.hybridContractedSharePct}
                      onChange={(e) => {
                        const contracted = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                        handleField("hybridContractedSharePct", contracted);
                        handleField("hybridMerchantSharePct", 100 - contracted);
                      }}
                      helperText="Portion of revenue or capacity under tolling contract."
                    />

                    <TextField
                      fullWidth
                      label="Contracted Toll Fee (€/MW-year)"
                      type="number"
                      value={cfg.hybridTollFeePerMWYear}
                      onChange={(e) =>
                        handleField("hybridTollFeePerMWYear", Number(e.target.value))
                      }
                      helperText="Fee applied to the contracted share."
                    />

                    <TextField
                      fullWidth
                      label="Merchant Share (%)"
                      type="number"
                      value={Math.max(0, 100 - Number(cfg.hybridContractedSharePct || 0))}
                      InputProps={{ readOnly: true }}
                      helperText="Automatically calculated as 100% minus Contracted Share."
                    />

                    <TextField
                      fullWidth
                      label="Contract Term (years)"
                      type="number"
                      value={cfg.tollingContractYears}
                      onChange={(e) =>
                        handleField("tollingContractYears", Number(e.target.value))
                      }
                      helperText="Duration of the partial tolling arrangement."
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 3,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        label="Settlement Frequency"
                        value={cfg.tollingSettlementFrequency}
                        onChange={(e) => handleField("tollingSettlementFrequency", e.target.value)}
                      >
                        <MenuItem value="Monthly">Monthly</MenuItem>
                        <MenuItem value="Quarterly">Quarterly</MenuItem>
                        <MenuItem value="Annually">Annually</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Annual Escalation (%)"
                      type="number"
                      value={cfg.tollingEscalationPct}
                      onChange={(e) =>
                        handleField(
                          "tollingEscalationPct",
                          Math.min(100, Math.max(0, Number(e.target.value) || 0))
                        )
                      }
                      helperText="Optional annual increase in contracted payment."
                    />

                    <FormControl fullWidth>
                      <InputLabel>Capacity Basis</InputLabel>
                      <Select
                        label="Capacity Basis"
                        value={cfg.tollingCapacityBasis}
                        onChange={(e) => handleField("tollingCapacityBasis", e.target.value)}
                      >
                        <MenuItem value="InstalledMW">Installed Power (MW)</MenuItem>
                        <MenuItem value="ContractedMW">Contracted Power (MW)</MenuItem>
                        <MenuItem value="AvailableMW">Available Power (MW)</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Merchant Optimization</InputLabel>
                      <Select
                        label="Merchant Optimization"
                        value={cfg.useMerchantOptimization ? "Yes" : "No"}
                        onChange={(e) => handleField("useMerchantOptimization", e.target.value === "Yes")}
                      >
                        <MenuItem value="Yes">Enabled</MenuItem>
                        <MenuItem value="No">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Best use case
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited when lenders require contracted revenue but equity seeks merchant upside.
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

          </Grid>


          <Divider sx={{ my: 3 }} />



          {/* Grid connection (level, voltage, import/export limits) moved to the
              dedicated Grid Connection page (/Grid). */}

        </AccordionDetails>
      </Accordion>

    </Paper>
  );
}
